import React, { useState } from 'react';
import { C, fmt, fmtCOP, OPERATORS, splitCU, excedentePriceFor } from '../constants';
import { fetchAgentsList, fetchSpotPrice } from '../services/xm';
import { aprobarTituloConte } from '../services/habilitacion';
import { searchCECPanels, searchCECInverters } from '../services/cec';
import { searchBatteries } from '../services/batteries';
import { fetchTRM } from '../services/trm';
import { fetchLoadsCatalog, DEFAULT_LOADS_CATALOG, invalidateLoadsCache } from '../services/loads';
import { hubConfigurado, hubApiBaseUrl, hubPost, hubGet } from '../services/hub';
import { fluxaiMonitoringUrl } from '../services/fluxai';
import { listaPreciosServicios, fijarPreciosServicios } from '../services/pagos';
import { useComisionSuite } from '../services/comision';
import { tokenParaBackend } from '../services/adminAuth';
import {
  listarUsuarios, crearUsuarioBasico, actualizarUsuario, setActivoUsuario,
  listarAsignaciones, asignarTecnico, desasignarTecnico, syncUsuarios,
  ROLES as USER_ROLES, ROL_LABELS,
} from '../services/usuarios';
import ServicesDx from './ServicesDx';
import PagosFacturacion from './PagosFacturacion';
import BackOfficeTienda from './BackOfficeTienda';
import Empresa from './Empresa';
import { tecnicoDeOpciones, ordenConTecnicoDeObra } from '../services/asignacion';
import {
  leerOfertasProveedores, bomDeCotizacion, cubrirBomConProveedor, itemsPoDesdeBom,
  fichasUnion, etiquetaOrigen, etiquetaTipo,
} from '../services/catalogo-equipo';
import {
  precioListaEquipos, precioPreferencialEquipos, baseInstalacionCotizacion, desgloseCierre, idRemotoCotizacion,
} from '../services/cierre';
import { DEFAULT_PANELS, DEFAULT_INVERTERS, DEFAULT_BATTERIES } from '../constants';

// Modal de búsqueda en la base CEC (NREL SAM) para importar equipos con
// specs eléctricos oficiales. onImport recibe el objeto normalizado y
// debe mapearlo al schema local (agregando precio, kg, etc. faltantes).
function CECImportModal({ type, onClose, onImport, ss }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const doSearch = async () => {
    if (!q.trim()) return;
    setLoading(true); setError(null);
    try {
      const fn = type === 'panel' ? searchCECPanels : searchCECInverters;
      const data = await fn(q, 25);
      setResults(data.results || []);
      setInfo({ total: data.total, count: data.count, cached: data.cached });
    } catch (e) {
      setError(e.message);
      setResults([]);
    }
    setLoading(false);
  };

  const onKey = e => { if (e.key === 'Enter') doSearch(); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, width: '100%', maxWidth: 760, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.heading }}>Importar desde CEC / NREL SAM</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Base oficial de California Energy Commission · {type === 'panel' ? 'paneles' : 'inversores'} certificados</div>
          </div>
          <button style={{ ...ss.btn, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }} onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}22`, display: 'flex', gap: 8 }}>
          <input autoFocus style={{ ...ss.inp, flex: 1 }} placeholder={type === 'panel' ? 'Ej: JA Solar 545, Canadian Solar 550, Trina 610…' : 'Ej: Growatt MIN, Solis 5K, SMA Sunny Boy…'} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} />
          <button style={ss.btn} onClick={doSearch} disabled={loading}>{loading ? 'Buscando…' : 'Buscar'}</button>
        </div>

        {error && <div style={{ padding: '10px 18px', color: C.red, fontSize: 11 }}> {error}</div>}
        {info && <div style={{ padding: '6px 18px', color: C.muted, fontSize: 10 }}>{info.count} de {fmt(info.total)} en base CEC {info.cached ? '· caché local' : ''}</div>}

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {results.length === 0 && !loading && !error && (
            <div style={{ padding: '40px 18px', textAlign: 'center', color: C.muted, fontSize: 12 }}>
              {q ? 'Escribe y busca para ver resultados.' : 'Base CEC: ~22.000 paneles y ~6.000 inversores certificados. Los resultados incluyen Voc, Vmp, Isc, Imp y coeficientes de temperatura oficiales.'}
            </div>
          )}
          {results.map((r, idx) => (
            <div key={idx} style={{ padding: '10px 18px', borderBottom: `1px solid ${C.border}22`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: C.heading, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.fullName || `${r.brand} ${r.model}`}</div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
                  {type === 'panel'
                    ? `${r.wp || '?'}Wp · Voc ${r.voc?.toFixed(1) || '?'}V · Vmp ${r.vmp?.toFixed(1) || '?'}V · Isc ${r.isc?.toFixed(2) || '?'}A · Imp ${r.imp?.toFixed(2) || '?'}A · ${r.cellCount || '?'} celdas · γ ${r.tempCoeffPmax?.toFixed(3) || '?'}%/°C`
                    : `${r.kw?.toFixed(1) || '?'}kW · ${r.phase}Φ ${r.vac}V · Vdc_max ${r.vocMax || '?'}V · MPPT ${r.mpptVmin || '?'}–${r.mpptVmax || '?'}V · Idc_max ${r.idcMax || '?'}A`
                  }
                </div>
              </div>
              <button style={{ ...ss.btn, padding: '5px 11px' }} onClick={() => onImport(r)}>+ Importar</button>
            </div>
          ))}
        </div>

        <div style={{ padding: '10px 18px', borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.muted }}>
          Fuente: NREL System Advisor Model · base actualizada trimestralmente · datos curados por CEC.
        </div>
      </div>
    </div>
  );
}

/**
 * Fila de edición del catálogo de equipos.
 *
 * 🔴 VIVE ACÁ, A NIVEL DE MÓDULO, A PROPÓSITO. Estaba declarada dentro de `EqMgr`, o sea que era
 * una función NUEVA en cada render: React la trataba como otro tipo de componente y **desmontaba y
 * volvía a montar los `<input>` en cada tecla** → el foco saltaba y el dueño tenía que hacer clic
 * de nuevo por cada carácter al editar un panel o un inversor. `EqMgr` se re-renderiza en cada
 * pulsación porque `form` es su estado, así que el efecto era en TODAS las teclas.
 * `src/__tests__/componentes-anidados.test.js` impide que vuelva adentro.
 */
function EqFormRow({ fields, form, setForm, save, cancel, ss }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, padding: '10px 0 6px' }}>
      {fields.map(f => (
        <div key={f.k}><label style={ss.lbl}>{f.l}</label>
          {f.t === 'select'
            ? <select style={{ ...ss.inp, cursor: 'pointer' }} value={form[f.k] || ''} onChange={e => setForm(p => ({ ...p, [f.k]: e.target.value }))}>{f.opts.map(o => <option key={o} value={o}>{o}</option>)}</select>
            : <input type={f.t} style={ss.inp} value={form[f.k] || ''} onChange={e => setForm(p => ({ ...p, [f.k]: f.t === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))} />}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        <button style={ss.btn} onClick={save}>Guardar</button>
        <button style={{ ...ss.btn, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }} onClick={cancel}>×</button>
      </div>
    </div>
  );
}

function FichasModeladoTabla({ fichas, ss }) {
  return (
    <div style={{ ...ss.card, marginTop: 14 }}>
      <div style={{ fontWeight: 600, color: C.heading, marginBottom: 8, fontSize: 13 }}>Fichas del modelado</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.45 }}>
        Lo que modela el cotizador: catálogo de la suite + inventario que cargan los proveedores.
      </div>
      {fichas.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 12 }}>Aún no hay fichas.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Origen', 'Equipo', 'Stock', 'Proveedor'].map((h) => <th key={h} style={ss.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {fichas.map((f) => (
              <tr key={f.id}>
                <td style={ss.td}>{etiquetaOrigen(f.origen)}</td>
                <td style={ss.td}>{f.brand} {f.model}{f.wp ? ` · ${f.wp} Wp` : f.kw ? ` · ${f.kw} kW` : f.kwh ? ` · ${f.kwh} kWh` : ''}</td>
                <td style={ss.td}>{f.stock?.qty ?? '—'}</td>
                <td style={ss.td}>{f.stock?.supplier || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BloqueFichasCotizacion({ quote, supplierId, ss }) {
  const bom = bomDeCotizacion(quote);
  if (!bom.length) return null;
  const oferta = supplierId
    ? leerOfertasProveedores().find((o) => String(o.id) === String(supplierId))
    : null;
  const cub = supplierId
    ? cubrirBomConProveedor(bom, oferta?.items || [], oferta?.company)
    : bom.map((l) => ({ ...l, cubierto: false, fuente: 'suite' }));
  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.teal}22` }}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, fontWeight: 600 }}>FICHAS DEL MODELADO · PROVEEDOR + SUITE</div>
      <div style={{ background: C.card, border: `1px solid ${C.border}33`, borderRadius: 5, padding: '8px 10px', fontSize: 11 }}>
        {cub.map((l) => (
          <div key={l.tipo + l.model} style={{ color: C.text, marginBottom: 3 }}>
            <span style={{ color: C.muted }}>{etiquetaTipo(l.tipo)}:</span>{' '}
            {l.brand} {l.model} × {l.qty}
            {l.wp ? ` · ${l.wp} Wp` : ''}{l.kw ? ` · ${l.kw} kW` : ''}{l.kwh ? ` · ${l.kwh} kWh` : ''}
            <span style={{ color: C.teal, marginLeft: 8 }}>{fmtCOP((l.qty || 1) * (l.unit_price_cop || l.price || 0))}</span>
            <span style={{ marginLeft: 8, fontSize: 10, color: l.cubierto ? C.teal : C.muted }}>
              {l.cubierto ? 'oferta proveedor' : 'ficha suite'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EqMgr({ title, items, upd, fields, ss }) {
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const shown = !q.trim() ? items
    : items.filter(it => fields.some(f => String(it[f.k] ?? '').toLowerCase().includes(q.toLowerCase().trim())));
  const startAdd = () => { setAdding(true); setEdit(null); setForm(Object.fromEntries(fields.map(f => [f.k, '']))); };
  const startEdit = i => { setEdit(i.id); setAdding(false); setForm({ ...i }); };
  const cancel = () => { setEdit(null); setAdding(false); setForm({}); };
  const save = () => { if (adding) upd([...items, { ...form, id: 'eq_' + Date.now() }]); else upd(items.map(i => i.id === edit ? { ...form } : i)); cancel(); };
  const del = id => upd(items.filter(i => i.id !== id));
  const filaForm = <EqFormRow fields={fields} form={form} setForm={setForm} save={save} cancel={cancel} ss={ss} />;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ ...ss.h2, margin: 0 }}>{title}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar…"
            style={{ ...ss.inp, width: 200, padding: '6px 10px' }} />
          <button style={ss.btn} onClick={startAdd}>+ Agregar</button>
        </div>
      </div>
      {adding && <div style={ss.card}>{filaForm}</div>}
      <div style={ss.card}>
        {q.trim() && <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>{shown.length} de {items.length}</div>}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{[...fields.map(f => f.l), ''].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
          <tbody>
            {shown.map(item => edit === item.id
              ? <tr key={item.id}><td colSpan={fields.length + 1} style={{ padding: 0 }}>{filaForm}</td></tr>
              : (<tr key={item.id}>
                {fields.map(f => <td key={f.k} style={ss.td}>{f.k === 'price' ? fmtCOP(item[f.k]) : item[f.k]}</td>)}
                <td style={ss.td}><div style={{ display: 'flex', gap: 5 }}><button style={ss.btn} onClick={() => startEdit(item)}>Editar</button><button style={ss.del} onClick={() => del(item.id)}>×</button></div></td>
              </tr>)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Los precios de los SERVICIOS (estudio, monitoreo, SCADA…) y de la VISITA TÉCNICA. No son los
 * costos de arriba: esta es la cifra con la que el checkout cobra. Vacío = no fijado, y entonces
 * eso NO se vende — el servidor devuelve `precio_no_fijado` en vez de cobrar lo que mande el
 * navegador.
 *
 * La visita entró acá el 7-sep-2026: su cifra vivía SOLO en `PRECIO_VISITA_TECNICA_COP`, o sea
 * que fijar el precio de lo único que el cotizador cobra pedía entrar a Railway y redesplegar.
 *
 * Y por eso se muestra la `fuente`: cuando manda una variable de entorno, lo que se escriba acá
 * queda guardado pero NO es lo que se cobra. Una casilla editable que no decide es una pantalla
 * que afirma un precio falso.
 */
function ServiciosPreciosCard({ ss }) {
  const [lista, setLista] = useState(null);
  const [form, setForm] = useState({});
  const [estado, setEstado] = useState({ cargando: true, error: null, guardado: false });

  const cargar = React.useCallback(() => {
    setEstado((e) => ({ ...e, cargando: true, error: null }));
    listaPreciosServicios()
      .then((r) => {
        if (!r || r.ok === false) throw new Error(r?.error || 'El hub-api no devolvió la lista.');
        setLista(r);
        setForm(Object.fromEntries(Object.entries(r.items || {}).map(([sku, it]) => [sku, it.precio_cop ?? ''])));
        setEstado({ cargando: false, error: null, guardado: false });
      })
      .catch((e) => setEstado({ cargando: false, error: e.message, guardado: false }));
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);

  const guardar = () => {
    const cambios = Object.fromEntries(
      Object.entries(form).map(([sku, v]) => [sku, v === '' || v == null ? null : v]),
    );
    setEstado((e) => ({ ...e, error: null }));
    fijarPreciosServicios(cambios)
      .then((r) => {
        if (!r || r.ok === false) throw new Error(r?.error || 'No se pudieron fijar los precios.');
        setLista(r);
        setEstado({ cargando: false, error: null, guardado: true });
        setTimeout(() => setEstado((e) => ({ ...e, guardado: false })), 2500);
      })
      .catch((e) => setEstado({ cargando: false, error: e.message, guardado: false }));
  };

  const items = Object.entries(lista?.items || {});
  return (
    <div style={{ ...ss.card, marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.heading, marginBottom: 4 }}>
        Precios de los servicios de la suite y de la visita técnica
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 13, lineHeight: 1.6 }}>
        Es la cifra con la que se cobra. Algo <b>sin precio no se vende</b>: el checkout lo
        rechaza en vez de cobrar el número que venga del navegador. El hardware no está acá — su
        precio sale del stock del proveedor.
      </div>

      {estado.error && (
        <div style={{ background: `${C.red}18`, border: `1px solid ${C.red}55`, borderRadius: 7, padding: '9px 12px', marginBottom: 12, fontSize: 11, color: C.text }}>
          {estado.error}
        </div>
      )}
      {estado.cargando && <div style={{ fontSize: 11, color: C.muted }}>Cargando…</div>}

      {!estado.cargando && !items.length && !estado.error && (
        <div style={{ fontSize: 11, color: C.muted }}>La lista llegó vacía.</div>
      )}

      {items.map(([sku, it]) => (
        <div key={sku} style={{ display: 'grid', gridTemplateColumns: '1fr 150px 96px', gap: 10, alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${C.borderLight}` }}>
          <div>
            <div style={{ fontSize: 12, color: C.text }}>{it.nombre}</div>
            <div style={{ fontSize: 10, color: C.muted }}>{sku} · por {it.unidad} · {it.suite}</div>
            {it.fuente === 'env' && (
              <div style={{ fontSize: 10, color: C.yellow }}>
                Hoy manda la variable {it.env || 'de entorno'} del hub-api: lo que guardes acá no
                será lo que se cobre hasta que esa variable se quite.
              </div>
            )}
          </div>
          <input
            type="number"
            min="0"
            placeholder="Sin fijar"
            style={ss.inp}
            value={form[sku] ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, [sku]: e.target.value }))}
          />
          <div style={{
            fontSize: 10, fontWeight: 600, textAlign: 'center', padding: '4px 6px', borderRadius: 6,
            background: it.precio_cop == null ? C.yL : C.tL,
            color: it.precio_cop == null ? C.yellow : C.teal,
          }}>
            {it.precio_cop == null ? 'sin fijar' : fmtCOP(it.precio_cop)}
          </div>
        </div>
      ))}

      {!!items.length && (
        <button style={{ ...ss.btn, padding: '9px 24px', marginTop: 14 }} onClick={guardar}>
          {estado.guardado ? '✓ Guardado' : 'Guardar precios'}
        </button>
      )}
    </div>
  );
}

function PriceMgr({ pricing, upd, ss }) {
  const [form, setForm] = useState(pricing);
  const [saved, setSaved] = useState(false);
  const [trmData, setTrmData] = useState(null);
  const [trmLoading, setTrmLoading] = useState(false);

  React.useEffect(() => {
    setTrmLoading(true);
    fetchTRM().then(d => setTrmData(d)).catch(() => {}).finally(() => setTrmLoading(false));
  }, []);

  const fields = [
    { k: 'structure_per_panel', l: 'Estructura/panel (COP)' },
    { k: 'cabling_per_kwp', l: 'Cableado/kWp' },
    { k: 'protections_per_kwp', l: 'Protecciones/kWp' },
    { k: 'installation_per_kwp', l: 'Instalación/kWp' },
    { k: 'engineering', l: 'Ingeniería fija (COP)' },
    { k: 'emsa_tramites', l: 'Trámites operador (COP)' },
    { k: 'iva', l: 'IVA Sección B (%)' },
  ];
  const sv = () => { upd(form); setSaved(true); setTimeout(() => setSaved(false), 2000); };
  return (
    <div>
      <div style={ss.h2}>Configuración de precios</div>
      {/* TRM widget */}
      <div style={{ background: `${C.teal}10`, border: `1px solid ${C.teal}33`, borderRadius: 7, padding: '9px 14px', marginBottom: 13, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>TRM (COP/USD)</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.yellow }}>
            {trmLoading ? '…' : trmData?.cop_per_usd ? `$${fmt(trmData.cop_per_usd)}` : '—'}
          </div>
        </div>
        {trmData && (
          <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.7 }}>
            <div>Vigencia: {trmData.date}</div>
            <div>Fuente: {trmData.source}</div>
            <div style={{ color: C.teal }}>Actualiza precios en USD → precio panel / TRM = precio COP</div>
          </div>
        )}
      </div>
      <div style={{ background: `${C.teal}08`, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 12px', marginBottom: 13, fontSize: 11, color: C.muted }}>
        Actualiza según cotizaciones reales del mercado. Los cambios aplican inmediatamente en el cotizador.
      </div>
      <div style={ss.card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          {fields.map(f => (
            <div key={f.k}><label style={ss.lbl}>{f.l}</label><input type="number" style={ss.inp} value={form[f.k]} onChange={e => setForm(p => ({ ...p, [f.k]: parseFloat(e.target.value) || 0 }))} /></div>
          ))}
        </div>
 <button style={{ ...ss.btn, padding: '9px 24px' }} onClick={sv}>{saved ? '✓ Guardado' : 'Guardar precios'}</button>
      </div>
      <ServiciosPreciosCard ss={ss} />
    </div>
  );
}

function QuotesMgr({ quotes, suppliers: suppliersList = [], installers = [], ordenes = [], updOrden, ss }) {
  // El % de reinversión lo manda la gestión (ver services/comision.js), no esta pantalla.
  const comision = useComisionSuite('instalacion');
  const [sel, setSel] = useState(null);
  const [assignTechId, setAssignTechId] = useState('');
  const [assigning, setAssigning] = useState(false);
  // Local overrides for tech assignment state (reflects optimistic updates without needing prop setter)
  const [techOverrides, setTechOverrides] = useState({});
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [loadingMatch, setLoadingMatch] = useState(false);
  // Los de la zona que el servidor NO sugiere porque root no los habilita (3-nov): se dice quiénes
  // y por qué, para que «no aparece nadie» no se lea como «no hay técnicos en la zona».
  const [matchExcluidos, setMatchExcluidos] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'kanban'
  const [creatingPO, setCreatingPO] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [poInstallAmt, setPoInstallAmt] = useState('');

  const handleCreatePO = async (quote, supplierId, installAmt) => {
    setCreatingPO(true);
    try {
      const oferta = leerOfertasProveedores().find((o) => String(o.id) === String(supplierId));
      const cubierto = cubrirBomConProveedor(
        bomDeCotizacion(quote),
        oferta?.items || [],
        oferta?.company || (suppliersList.find((s) => String(s.id) === String(supplierId)) || {}).company,
      );
      const items = itemsPoDesdeBom(cubierto);
      const preferencial = precioPreferencialEquipos(quote, items.reduce((s, i) => s + (i.line_total_cop || 0), 0));
      const lista = precioListaEquipos(quote);
      const instalacion = Number(installAmt) > 0 ? Number(installAmt) : baseInstalacionCotizacion(quote);
      const quoteId = idRemotoCotizacion(quote.id) || quote.id;
      const data = await hubPost('supplier-po', {
        method: 'POST_CREATE',
        supplier_id: supplierId,
        quote_id: quoteId,
        technician_id: quote.technician_id || null,
        items,
        subtotal_equipment: preferencial,
        list_equipment: lista,
        installation_total: instalacion,
      });
      if (data.ok) { alert(`✓ Orden ${data.po_number} creada`); }
      else { alert('Error: ' + (data.error || 'no se pudo crear')); }
    } catch (e) { alert(e?.message || 'Error de red'); } finally { setCreatingPO(false); }
  };

  const handleFindMatches = async (quote) => {
    setLoadingMatch(true);
    setMatchSuggestions([]);
    setMatchExcluidos([]);
    try {
      const data = await hubPost('matching-installer', {
        quote_id: quote.id, dept: quote.dept, kwp: quote.kwp || quote.results?.actKwp || 5,
        // Sugerir instaladores es administración: el servidor la gatea con `exigeAdmin`, y
        // `hubPost` no manda la credencial del usuario. Sin esta línea el botón responde 401.
        token: tokenParaBackend() || '',
      });
      setMatchSuggestions(data.matches || []);
      setMatchExcluidos(Array.isArray(data.excluidos) ? data.excluidos : []);
    } catch(e) { console.error('matching error', e); }
    setLoadingMatch(false);
  };

  const handleAssignTech = async (selectedQuote) => {
    if (!assignTechId || !selectedQuote?.id) return;
    const tech = tecnicoDeOpciones(installers, matchSuggestions).find((t) => String(t.id) === String(assignTechId));
    setAssigning(true);
    try {
      const data = await hubPost('assign-technician', {
        quoteId: selectedQuote.id, technicianId: Number(assignTechId),
        // Asignar técnico reparte el `tech_token` de la cotización: es administración.
        token: tokenParaBackend() || '',
      });
      if (data && data.ok === false) {
        alert('Error al asignar técnico. Intente de nuevo.');
        return;
      }
      setTechOverrides(prev => ({
        ...prev,
        [selectedQuote.id]: {
          technician_id: Number(assignTechId),
          technician_name: tech?.name || '',
          doc_status: 'en_revision',
          status: 'asignado',
          tech_token: data?.techToken || data?.tech_token,
        },
      }));
      const orden = ordenConTecnicoDeObra(ordenes, selectedQuote.id, tech);
      if (orden && updOrden) updOrden(orden);
    } catch (e) {
      console.error('assign tech error', e);
      if (tech) {
        setTechOverrides(prev => ({
          ...prev,
          [selectedQuote.id]: { technician_id: tech.id, technician_name: tech.name, status: 'asignado' },
        }));
        const orden = ordenConTecnicoDeObra(ordenes, selectedQuote.id, tech);
        if (orden && updOrden) updOrden(orden);
      } else {
        alert('Error de red al asignar técnico.');
      }
    } finally {
      setAssigning(false);
    }
  };

  const handleExportCSV = () => {
    if (!quotes || quotes.length === 0) return;
    const headers = ['ID', 'Fecha', 'Estado', 'Nombre', 'Email', 'Teléfono', 'Empresa', 'Departamento', 'kWp', 'Paneles', 'Total COP', 'Ahorro anual COP', 'ROI años', 'Tipo sistema', 'Doc. estado'];
    const rows = quotes.map(q => [
      q.id,
      q.date || (q.created_at ? new Date(q.created_at).toLocaleDateString('es-CO') : ''),
      q.status || '',
      q.name || '',
      q.email || '',
      q.phone || '',
      q.company || '',
      q.dept || '',
      q.results?.actKwp || q.kwp || '',
      q.results?.numPanels || q.num_panels || '',
      q.budget?.tot || q.total_cop || '',
      q.budget?.sav || q.annual_sav_cop || '',
      q.budget?.roi || q.roi_years || '',
      q.systemType || q.system_type || '',
      q.doc_status || '',
    ]);
    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cotizaciones_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Flujo guiado post-venta: al ABRIR una cotización GANADA sin técnico asignado,
  // auto-sugiere instaladores (corre el matching) para no tener que recordarlo.
  React.useEffect(() => {
    if (!sel) return;
    const cur = quotes.find(x => x.id === sel);
    const merged = cur && techOverrides[sel] ? { ...cur, ...techOverrides[sel] } : cur;
    const hasTech = merged && (merged.technician_id || merged.status === 'asignado');
    if (merged && merged.status === 'ganada' && !hasTech && matchSuggestions.length === 0 && !loadingMatch) {
      handleFindMatches(merged);
    }
  }, [sel]); // eslint-disable-line

  if (sel) {
    const qBase = quotes.find(x => x.id === sel);
    if (!qBase) { setSel(null); return null; }
    // Merge local tech assignment overrides with base quote data
    const q = techOverrides[sel] ? { ...qBase, ...techOverrides[sel] } : qBase;
    // Opciones de técnico REALES: instaladores registrados + sugerencias del matching
    // (con score). Reemplaza el dropdown hardcodeado. Clave por id, el matching pisa.
    const techOptions = tecnicoDeOpciones(installers, matchSuggestions);
    return (
      <div>
        <button style={{ ...ss.btn, marginBottom: 12, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }} onClick={() => setSel(null)}>← Volver</button>
        <div style={ss.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div><div style={{ fontSize: 16, fontWeight: 700, color: C.heading }}>{q.name}</div><div style={{ color: C.muted, fontSize: 11 }}>{q.company && `${q.company} · `}{q.city} · {q.operator} · {q.date}</div></div>
            <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: `${C.teal}22`, color: C.teal }}>{q.status}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
            {[['Email', q.email], ['Teléfono', q.phone], ['Dirección', q.address || '—'], ['Tipo', q.systemType], ['Consumo', `${q.monthlyKwh} kWh/mes`], ['Operador', q.operator], ['kWp', `${q.results?.actKwp} kWp`], ['Paneles', `${q.results?.numPanels}`], ['Producción', `${fmt(q.results?.mp || 0)} kWh/mes`], ['Cobertura', `${q.results?.cov}%`], ['CO2 evitado', `${fmt(q.results?.co2 || 0)} kg/año`], ['Total inversión', q.budget ? fmtCOP(q.budget.tot) : '—'], ['Sección A', q.budget ? fmtCOP(q.budget.sA) : '—'], ['Sección B', q.budget ? fmtCOP(q.budget.sB) : '—'], ['Transporte', q.budget ? fmtCOP(q.budget.transport) : '—'], ['Ahorro anual', q.budget ? fmtCOP(q.budget.sav) : '—'], ['ROI', q.budget ? `${q.budget.roi} años` : '—']].map(([k, v]) => (
              <div key={k} style={{ padding: '5px 0', borderBottom: `1px solid ${C.border}22` }}>
                <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.3 }}>{k}</div>
                <div style={{ fontSize: 11, color: C.heading, fontWeight: 500, marginTop: 1 }}>{v}</div>
              </div>
            ))}
          </div>

          <BloqueFichasCotizacion quote={q} supplierId={selectedSupplierId} ss={ss} />

          {/* ── Siguiente paso (flujo guiado post-venta) ── */}
          {q.status === 'ganada' && (() => {
            const hasTech = !!(q.technician_id || q.status === 'asignado' || q.tech_token);
            const hasPO = !!q.po_id;
            const steps = [
              { n: 1, done: true, label: 'Venta ganada' },
              { n: 2, done: hasTech, label: hasTech ? 'Técnico asignado' : 'Asignar técnico', hint: !hasTech && (loadingMatch ? 'Buscando instaladores…' : 'Elige un instalador sugerido abajo') },
              { n: 3, done: hasPO, label: hasPO ? `Orden ${q.po_number || ''} creada` : 'Crear orden de compra', hint: !hasPO && hasTech ? 'Pre-armada con los equipos de la cotización abajo' : (!hasTech ? 'Disponible al asignar técnico' : '') },
            ];
            return (
              <div style={{ marginTop: 16, padding: '12px 14px', background: `${C.yellow}0d`, border: `1px solid ${C.yellow}33`, borderRadius: 9 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.yellow, marginBottom: 10 }}> Siguiente paso</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {steps.map(s => (
                    <div key={s.n} style={{ flex: '1 1 160px', minWidth: 160, background: C.dark, border: `1px solid ${s.done ? '#00c78144' : C.border}`, borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <span style={{ width: 18, height: 18, borderRadius: '50%', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', background: s.done ? '#00c781' : `${C.muted}33`, color: s.done ? '#04130c' : C.muted }}>{s.done ? '✓' : s.n}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: s.done ? C.heading : C.text }}>{s.label}</span>
                      </div>
                      {s.hint && <div style={{ fontSize: 10, color: C.muted, marginTop: 4, lineHeight: 1.3 }}>{s.hint}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── Technician Assignment ── */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.teal}22` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>ASIGNACIÓN TÉCNICA</div>
              <button
                onClick={() => handleFindMatches(q)}
                disabled={loadingMatch}
                style={{ fontSize: 11, fontWeight: 600, color: C.teal, background: `${C.teal}18`, border: `1px solid ${C.teal}44`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
              >
                {loadingMatch ? 'Buscando...' : ' Sugerir instaladores'}
              </button>
            </div>

            {matchSuggestions.length > 0 && (
              <div style={{ background: C.dark, border: `1px solid ${C.teal}22`, borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Mejores coincidencias</div>
                {matchSuggestions.slice(0, 3).map((m, i) => (
                  <div key={m.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < 2 ? `1px solid ${C.teal}11` : 'none' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: C.heading, fontWeight: 600 }}>{m.name}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>
 {'★'.repeat(Math.round(parseFloat(m.rating_avg) || 0))} {m.rating_avg ? parseFloat(m.rating_avg).toFixed(1) : 'N/A'} · {m.active_jobs || 0} activos
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, background: `${C.teal}18`, borderRadius: 12, padding: '2px 8px' }}>{m.score}</span>
                      <button
                        onClick={() => setAssignTechId(String(m.id))}
                        style={{ fontSize: 10, fontWeight: 600, color: C.yellow, background: `${C.yellow}18`, border: `1px solid ${C.yellow}44`, borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}
                      >
                        Asignar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {matchExcluidos.length > 0 && (
              <div data-matching-excluidos style={{ background: `${C.yellow}0d`, border: `1px solid ${C.yellow}33`, borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.yellow, marginBottom: 4 }}>
                  {matchExcluidos.length} técnico{matchExcluidos.length === 1 ? '' : 's'} de la zona sin sugerir: no {matchExcluidos.length === 1 ? 'está habilitado' : 'están habilitados'} en root
                </div>
                {matchExcluidos.slice(0, 5).map((x) => (
                  <div key={x.id} style={{ fontSize: 10, color: C.muted, lineHeight: 1.5 }}>
                    {x.name} · {(x.faltan && x.faltan.length ? `falta: ${x.faltan.join(', ')}` : x.motivo)}
                  </div>
                ))}
              </div>
            )}

            <select
              value={assignTechId}
              onChange={e => setAssignTechId(e.target.value)}
              style={{ width: '100%', background: C.card, color: C.text, border: `1px solid ${C.teal}44`, borderRadius: 6, padding: '6px 10px', fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }}
            >
              <option value="">Seleccionar técnico...</option>
              {techOptions.map(t => (
                <option key={t.id} value={String(t.id)}>{t.name}{t.sub ? ` — ${t.sub}` : ''}</option>
              ))}
              {techOptions.length === 0 && <option disabled>Sin instaladores — usa "Sugerir instaladores"</option>}
            </select>

            <button
              onClick={() => handleAssignTech(q)}
              disabled={!assignTechId || assigning}
              style={{ width: '100%', background: assignTechId ? C.teal : '#333', color: C.heading, border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: assignTechId ? 'pointer' : 'not-allowed', marginBottom: 8 }}
            >
 {assigning ? 'Asignando...' : ' Asignar técnico y generar documentos'}
            </button>

            {q.tech_token && (
              <div style={{ fontSize: 11, color: C.teal, background: `${C.teal}11`, padding: '6px 10px', borderRadius: 5, marginBottom: 8 }}>
                 Portal técnico: {window.location.origin}/?view=tecnico&token={q.tech_token}
                <button
                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/?view=tecnico&token=${q.tech_token}`)}
                  style={{ marginLeft: 8, fontSize: 10, color: C.yellow, background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Copiar
                </button>
              </div>
            )}

            {/* Monitoreo en FluxAI — telemetría real (M&V medido vs simulado) del sistema instalado.
                Visible al ganar la venta (post-venta). El SSO sin re-login llega con el login de api/. */}
            {q.status === 'ganada' && (
              <a href={fluxaiMonitoringUrl(q)} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', textAlign: 'center', textDecoration: 'none', background: `${C.teal}22`, color: C.teal, border: `1px solid ${C.teal}44`, borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                 Ver monitoreo en FluxAI
              </a>
            )}

            {q.doc_status && q.doc_status !== 'pendiente' && (
              <div style={{ marginTop: 6, fontSize: 11, padding: '4px 10px', borderRadius: 20, display: 'inline-block',
                background: q.doc_status === 'aprobado' ? `${C.teal}22` : `${C.orange || '#EB5D0B'}22`,
                color: q.doc_status === 'aprobado' ? C.teal : C.orange || '#EB5D0B',
                border: `1px solid ${q.doc_status === 'aprobado' ? C.teal : C.orange || '#EB5D0B'}44`,
              }}>
 {q.doc_status === 'aprobado' ? '✓ Documentos aprobados por técnico' :
                 q.doc_status === 'en_revision' ? ' En revisión técnica' :
                 q.doc_status === 'cambios_solicitados' ? '↩ Técnico solicitó cambios' : q.doc_status}
              </div>
            )}
          </div>

          {/* ── Orden de Compra ── */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.teal}22` }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, fontWeight: 600 }}>ORDEN DE COMPRA</div>
            {q.po_id ? (
              <div style={{ background: `${C.teal}11`, border: `1px solid ${C.teal}33`, borderRadius: 7, padding: '10px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>N° Orden</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.teal }}>{q.po_number || q.po_id}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Estado</div>
                    <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: `${C.yellow}22`, color: C.yellow }}>{q.po_status || 'pendiente'}</span>
                  </div>
                  {q.supplier_id && (
                    <div>
                      <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Proveedor</div>
                      <div style={{ fontSize: 11, color: C.heading }}>{(suppliersList.find(s => s.id === q.supplier_id) || {}).company || `#${q.supplier_id}`}</div>
                    </div>
                  )}
                </div>
              </div>
            ) : (q.status === 'aprobado' || q.status === 'asignado' || q.technician_id) ? (
              <div style={{ background: C.dark, border: `1px solid ${C.border}`, borderRadius: 7, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>Crear orden de compra para esta cotización:</div>

                {/* Supplier dropdown */}
                <div style={{ marginBottom: 8 }}>
                  <label style={ss.lbl}>Proveedor</label>
                  <select
                    value={selectedSupplierId}
                    onChange={e => setSelectedSupplierId(e.target.value)}
                    style={{ width: '100%', background: C.card, color: C.text, border: `1px solid ${C.teal}44`, borderRadius: 6, padding: '6px 10px', fontSize: 12, boxSizing: 'border-box' }}
                  >
                    <option value="">Seleccionar proveedor...</option>
                    {suppliersList.map(s => (
                      <option key={s.id} value={s.id}>{s.company}{s.category ? ` — ${s.category}` : ''}</option>
                    ))}
                    {suppliersList.length === 0 && <option disabled>Sin proveedores registrados</option>}
                  </select>
                </div>

                {/* Installation amount */}
                <div style={{ marginBottom: 10 }}>
                  <label style={ss.lbl}>Instalación que cobra el técnico (COP)</label>
                  <input
                    type="number"
                    style={{ width: '100%', background: C.card, color: C.text, border: `1px solid ${C.teal}44`, borderRadius: 6, padding: '6px 10px', fontSize: 12, boxSizing: 'border-box' }}
                    placeholder={String(baseInstalacionCotizacion(q) || '')}
                    value={poInstallAmt}
                    onChange={e => setPoInstallAmt(e.target.value)}
                  />
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                    Él cobra esto. El 80% es su neto. El 20% se reinvierte (no es ganancia neta).
                  </div>
                </div>

                {(() => {
                  const bom = itemsPoDesdeBom(cubrirBomConProveedor(
                    bomDeCotizacion(q),
                    (leerOfertasProveedores().find((o) => String(o.id) === String(selectedSupplierId)) || {}).items || [],
                    '',
                  ));
                  const d = desgloseCierre({
                    lista: precioListaEquipos(q),
                    preferencial: precioPreferencialEquipos(q, bom.reduce((s, i) => s + (i.line_total_cop || 0), 0)),
                    instalacion: parseInt(poInstallAmt || baseInstalacionCotizacion(q) || 0, 10),
                    cargoTecPct: comision.pct,
                  });
                  if (!d.equipos && !d.instalacion) return null;
                  return (
                  <div style={{ background: C.card, border: `1px solid ${C.border}33`, borderRadius: 5, padding: '8px 10px', marginBottom: 10, fontSize: 10 }}>
                    <div style={{ color: C.muted, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Desglose financiero</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 12px' }}>
                      <div style={{ color: C.muted }}>Lista (cliente):</div><div style={{ color: C.text }}>{fmtCOP(d.lista)}</div>
                      <div style={{ color: C.muted }}>Preferencial (se paga 100%):</div><div style={{ color: C.teal }}>{fmtCOP(d.preferencial)}</div>
                      <div style={{ color: C.muted }}>Margen Alebas:</div><div style={{ color: C.text }}>{fmtCOP(d.margen)}</div>
                      <div style={{ color: C.muted }}>Reserva admin (60% margen):</div><div style={{ color: C.yellow }}>{fmtCOP(d.gastosAdmin)}</div>
                      <div style={{ color: C.muted }}>Reserva mant. plataforma (40%):</div><div style={{ color: C.yellow }}>{fmtCOP(d.mantPlataforma)}</div>
                      <div style={{ color: C.muted }}>Instalación (él la cobra):</div><div style={{ color: C.text }}>{fmtCOP(d.instalacion)}</div>
                      <div style={{ color: C.muted }}>Reinversión {comision.pct}% (no es ganancia):</div><div style={{ color: C.yellow }}>{fmtCOP(d.cargoAdminTec)}</div>
                      <div style={{ color: C.muted }}>Neto del técnico {comision.neto_pct}%:</div><div style={{ color: C.teal }}>{fmtCOP(d.techEarnings)}</div>
                    </div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 8, lineHeight: 1.4 }}>
                      El técnico cobra la instalación: el {comision.neto_pct}% es suyo. El {comision.pct}% no es ganancia neta de Solar Hub:
                      se reinvierte en infraestructura, capacitaciones y mantener al día las herramientas de modelado.
                      El proveedor da precio preferencial y se le paga entero.
                    </div>
                  </div>
                  );
                })()}

                <button
                  onClick={() => handleCreatePO(q, Number(selectedSupplierId), parseInt(poInstallAmt || baseInstalacionCotizacion(q) || 0, 10))}
                  disabled={!selectedSupplierId || creatingPO}
                  style={{ width: '100%', background: selectedSupplierId ? C.yellow : '#333', color: selectedSupplierId ? '#07090F' : C.heading, border: 'none', borderRadius: 6, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: selectedSupplierId ? 'pointer' : 'not-allowed' }}
                >
                  {creatingPO ? 'Creando orden...' : ' Crear Orden de Compra'}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>
                Disponible cuando la cotización esté en estado <strong>aprobado</strong> o <strong>asignado</strong>.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 🔴 ESTAS COLUMNAS NO COINCIDÍAN CON NINGÚN ESTADO REAL, y el filtro es igualdad exacta
  // (`q.status === key`, más abajo). El catálogo del servidor dice `ganada`, y acá la columna se
  // llamaba `ganado`; `aprobado` y `en_revision` no existen en ningún catálogo. Resultado medido al
  // cruzar las listas el 15-sep: **una cotización ganada no aparecía en NINGUNA columna** — se
  // perdía de vista justo al cerrarse la venta, y el tablero se veía normal.
  // Las claves salen ahora del mismo catálogo que valida `update-quote` y que dispara los avisos
  // (`server/estados-cotizacion.js`); `estados-una-sola-lista.test.js` cruza las dos listas.
  const KANBAN_COLS = [
    { key: 'nuevo',                 label: 'Nuevo',        color: C.muted },
    { key: 'contactado',            label: 'Contactado',   color: C.muted },
    { key: 'propuesta',             label: 'Propuesta',    color: C.yellow },
    { key: 'negociacion',           label: 'Negociación',  color: C.yellow },
    { key: 'ganada',                label: 'Ganada ✓',     color: C.teal },
    { key: 'asignado',              label: 'Asignado',     color: C.orange },
    { key: 'instalacion_agendada',  label: 'Agendada',     color: C.orange },
    { key: 'instalacion_terminada', label: 'Instalada ✓',  color: '#4ade80' },
  ];

  return (
    <div>
      {/* Header row: title + controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={ss.h2}>Cotizaciones ({quotes.length})</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            onClick={handleExportCSV}
            style={{
              background: 'none', border: `1px solid ${C.teal}66`, color: C.teal,
              borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}
          >
            ↓ Exportar CSV
          </button>
          <button
            onClick={() => setViewMode(m => m === 'list' ? 'kanban' : 'list')}
            style={{
              background: viewMode === 'kanban' ? `${C.teal}22` : 'none',
              border: `1px solid ${C.teal}66`, color: C.teal,
              borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}
          >
 {viewMode === 'list' ? '⬛ Kanban' : '≡ Lista'}
          </button>
        </div>
      </div>

      {quotes.length === 0 ? (
        <div style={{ ...ss.card, textAlign: 'center', padding: '44px', color: C.muted }}>Las cotizaciones del portal aparecerán aquí automáticamente.</div>
      ) : viewMode === 'kanban' ? (
        /* ── Kanban / Pipeline view ── */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, padding: '0 0 16px', overflowX: 'auto' }}>
          {KANBAN_COLS.map(({ key, label, color }) => {
            const colQuotes = quotes.filter(q => (q.status || 'nuevo') === key);
            return (
              <div key={key} style={{ background: C.card, borderRadius: 8, border: `1px solid ${color}44`, minHeight: 200 }}>
                {/* Column header */}
                <div style={{ padding: '8px 12px', borderBottom: `2px solid ${color}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color }}>{label}</div>
                  <div style={{ fontSize: 11, color: C.muted, background: `${color}22`, borderRadius: 10, padding: '1px 7px' }}>{colQuotes.length}</div>
                </div>
                {/* Cards */}
                <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {colQuotes.slice(0, 10).map(q => (
                    <div
                      key={q.id}
                      onClick={() => setSel(q.id)}
                      style={{ background: '#07090F', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', border: `1px solid ${color}22`, transition: 'border-color 0.2s' }}
                      onMouseOver={e => { e.currentTarget.style.borderColor = color; }}
                      onMouseOut={e => { e.currentTarget.style.borderColor = `${color}22`; }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.text, marginBottom: 2 }}>{q.name || q.email}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>
                        {q.results?.actKwp ? `${q.results.actKwp} kWp` : q.kwp ? `${q.kwp} kWp` : ''}
                        {(q.dept || q.results?.actKwp || q.kwp) && q.dept ? ` · ${q.dept}` : ''}
                      </div>
                      <div style={{ fontSize: 10, color, marginTop: 3 }}>
                        {q.budget?.tot
                          ? `$${Number(q.budget.tot).toLocaleString('es-CO')}`
                          : q.total_cop
                          ? `$${Number(q.total_cop).toLocaleString('es-CO')}`
                          : '—'}
                      </div>
                    </div>
                  ))}
                  {colQuotes.length > 10 && (
                    <div style={{ fontSize: 10, color: C.muted, textAlign: 'center', padding: 4 }}>
                      +{colQuotes.length - 10} más
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── List view (unchanged) ── */
        <div style={ss.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Fecha', 'Cliente', 'Operador', 'Sistema', 'kWp', 'Inversión', ''].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
            <tbody>{quotes.map(q => (
              <tr key={q.id}>
                <td style={ss.td}>{q.date}</td><td style={ss.td}>{q.name}</td><td style={ss.td}>{q.operator || '—'}</td>
                <td style={ss.td}>{q.systemType}</td><td style={ss.td}>{q.results?.actKwp}</td>
                <td style={ss.td}>{q.budget ? fmtCOP(q.budget.tot) : '—'}</td>
                <td style={ss.td}><button style={{ ...ss.btn, padding: '3px 9px' }} onClick={() => setSel(q.id)}>Ver →</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CRED_STATUS_BADGE = {
  pendiente:   { bg: `${C.muted}22`,   color: C.muted,  icon: '' },
  en_revision: { bg: '#00959922',       color: C.teal,   icon: '' },
  aprobado:    { bg: '#4ade8022',       color: '#4ade80', icon: '' },
  rechazado:   { bg: '#f8717122',       color: '#f87171', icon: '' },
  vencido:     { bg: '#F9B00022',       color: C.amber,  icon: '⏰' },
};

/**
 * VÍA MANUAL DEL TÍTULO: la tarjeta CONTE. Verifik consulta el COPNIA pero no el CONTE (técnicos
 * electricistas, Ley 19 de 1990), así que esa tarjeta la revisa un admin acá y queda registrado
 * quién y cuándo. Desde ahí cuenta como título para la habilitación del técnico (corte 1-nov-2026).
 * Vive a nivel de módulo: dentro de otro componente se remontaría en cada render.
 */
function TituloConteManual({ tec }) {
  const [estado, setEstado] = useState({
    via: tec.titulo_via || null, por: tec.titulo_aprobado_por || null, en: tec.titulo_aprobado_en || null,
  });
  const [notas, setNotas] = useState('');
  const [msg, setMsg] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const aprobado = estado.via === 'conte_manual';

  const decidir = async (aprobar) => {
    setOcupado(true);
    setMsg('');
    try {
      const r = await aprobarTituloConte({ technicianId: tec.id, aprobar, notas: notas || null });
      const t = (r && r.tecnico) || {};
      setEstado({ via: t.titulo_via || null, por: t.titulo_aprobado_por || null, en: t.titulo_aprobado_en || null });
      setMsg(aprobar ? 'Título CONTE aprobado: cuenta como título para operar.' : 'Aprobación del CONTE retirada.');
    } catch (e) {
      setMsg((e && e.data && e.data.error) || (e && e.message) || 'No se pudo registrar.');
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div style={{ background: C.dark, borderRadius: 8, padding: '10px 12px', marginBottom: 14, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Título por CONTE (revisión manual)</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.45 }}>
        Verifik no consulta el CONTE. Revisa la tarjeta {tec.conte_number} en los documentos de arriba y deja
        constancia: desde el 1-nov-2026 sin título verificado el técnico no opera.
      </div>
      {aprobado && (
        <div style={{ fontSize: 11, color: '#4ade80', marginTop: 6 }}>
          Aprobado{estado.por ? ` por ${estado.por}` : ''}{estado.en ? ` el ${new Date(estado.en).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}` : ''}.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Nota de la revisión (opcional)"
          style={{ flex: '1 1 200px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: '6px 8px', fontSize: 12 }} />
        {aprobado ? (
          <button type="button" disabled={ocupado} onClick={() => decidir(false)} style={{
            background: '#f8717122', color: '#f87171', border: '1px solid #f8717144', borderRadius: 8,
            padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600, opacity: ocupado ? 0.5 : 1,
          }}>Retirar aprobación</button>
        ) : (
          <button type="button" disabled={ocupado} onClick={() => decidir(true)} style={{
            background: '#4ade8022', color: '#4ade80', border: '1px solid #4ade8044', borderRadius: 8,
            padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600, opacity: ocupado ? 0.5 : 1,
          }}>Aprobar título CONTE</button>
        )}
      </div>
      {msg && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{msg}</div>}
    </div>
  );
}

function CredReviewPanel({ inst, onClose, ss }) {
  const [credData, setCredData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const loadCreds = async () => {
    if (!inst.id) return;
    setLoading(true);
    try {
      // Los documentos del instalador (CONTE, hoja de vida…) solo salen con sesión firmada: el
      // servidor responde 401 sin token desde el 15-sep (antes los servía por id consecutivo).
      const data = await hubPost('installer-credentials', {
        method: 'GET', technician_id: inst.id, token: tokenParaBackend() || '',
      });
      setCredData(data);
    } catch (e) {
      setCredData({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  // Auto-load on mount (empty deps intentional — run once)
  React.useEffect(() => { loadCreds(); }, []); // eslint-disable-line

  const review = async (status) => {
    setSaving(true);
    try {
      await hubPost('installer-credentials', {
        method: 'REVIEW',
        technician_id: inst.id,
        status,
        notes: rejectReason || null,
        verified_by: 'admin',
        // Aprobar una credencial RETIE es una acción de administración: el servidor la gatea con
        // `exigeAdmin`. `hubPost` manda el secreto compartido del servicio, no la credencial del
        // usuario, así que el token de la sesión va acá — y desde el 2026-08-29 tiene que ser el
        // token FIRMADO del admin: `getToken()` devuelve el `alg:none` de la suite, que da 401.
        token: tokenParaBackend() || '',
      });
      setResult(status);
      setShowReject(false);
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const openDoc = (doc) => {
    if (!doc.file_data_b64) return;
    const ext = doc.file_name?.split('.').pop()?.toLowerCase() || 'pdf';
    const mime = ext === 'pdf' ? 'application/pdf' : `image/${ext}`;
    const byteChars = atob(doc.file_data_b64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: mime });
    window.open(URL.createObjectURL(blob), '_blank');
  };

  // 🔴 El servidor devuelve { ok, technician: { ...ficha, documents } } y esta pantalla leía los campos
  // en la RAÍZ: el revisor veía «Sin documentos subidos aún» y sin número CONTE/COPNIA, y podía aprobar
  // a ciegas. Se lee de `technician`; la raíz queda como respaldo por si otra ruta la aplana.
  const tec = credData?.technician || credData || {};
  const docs = tec.documents || [];
  const credStatus = result || tec.credential_status || inst.credential_status || 'pendiente';
  const badge = CRED_STATUS_BADGE[credStatus] || CRED_STATUS_BADGE.pendiente;

  return (
    <div style={{ marginTop: 16, background: '#111f35', borderRadius: 12, padding: 18, border: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
           Verificación RETIE — {inst.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ background: badge.bg, color: badge.color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
            {badge.icon} {credStatus}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
      </div>

      {loading && <div style={{ color: C.muted, fontSize: 12, textAlign: 'center', padding: '20px 0' }}>Cargando documentos...</div>}

      {!loading && credData?.ok === false && (
        <div style={{ color: '#f87171', fontSize: 12 }}>Error cargando datos: {credData.error || 'sin datos'}</div>
      )}

      {!loading && credData?.ok && (
        <>
          {/* Empresa instaladora: la habilita root por NIT, no este panel */}
          {tec.installer_type === 'empresa' && (
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Empresa instaladora{tec.numero_documento ? ` · NIT ${tec.numero_documento}` : ' · sin NIT registrado'}
              {' · '}la habilita ALEBAS (root) con su expediente de contratista y el mismo correo; sus papeles no se revisan aquí.
            </div>
          )}

          {/* Credential number */}
          {tec.installer_type !== 'empresa' && (tec.copnia_number || tec.conte_number) && (
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              {tec.installer_type === 'ingeniero'
                ? `COPNIA: ${tec.copnia_number}`
                : `CONTE: ${tec.conte_number}`}
              {' · '}{tec.installer_type === 'ingeniero' ? 'Ingeniero' : 'Técnico Electricista'}
            </div>
          )}

          {tec.conte_number && tec.id && <TituloConteManual tec={tec} />}

          {/* Document list */}
          {docs.length === 0 ? (
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>Sin documentos subidos aún.</div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              {docs.map((doc, idx) => (
                <div key={doc.id || idx} style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
                  background: C.dark, borderRadius: 8, padding: '8px 12px',
                }}>
                  <span style={{ fontSize: 16 }}></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: C.text }}>{doc.file_name}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{doc.doc_type} · {doc.file_size_kb} KB</div>
                    {doc.doc_type === 'certificado_vigencia' && (() => {
                      const f = String(doc.fecha_expedicion || '').slice(0, 10);
                      const n = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(f)
                        ? Math.round((Date.now() - Date.parse(`${f}T00:00:00-05:00`)) / 86400000) : null;
                      const vigente = n != null && n >= 0 && n <= 30;
                      return (
                        <div style={{ fontSize: 11, fontWeight: 600, color: vigente ? '#4ade80' : '#f87171' }}>
                          {n == null ? 'Sin fecha de expedición: no se puede aprobar' : `Expedido el ${f} · hace ${n} días${vigente ? '' : ' · VENCIDO (máx. 30)'}`}
                        </div>
                      );
                    })()}
                  </div>
                  {doc.file_data_b64 && (
                    <button onClick={() => openDoc(doc)} style={{
                      background: C.teal, color: C.heading, border: 'none', borderRadius: 6,
                      padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'Outfit',
                    }}>Ver</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Admin notes */}
          {tec.credential_notes && (
            <div style={{ background: '#f8717118', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#f87171', marginBottom: 12 }}>
              Razón rechazo anterior: {tec.credential_notes}
            </div>
          )}

          {/* Action buttons */}
          {result ? (
            <div style={{ color: result === 'aprobado' ? '#4ade80' : '#f87171', fontSize: 13, fontWeight: 600 }}>
              {result === 'aprobado' ? ' Credenciales aprobadas' : ' Credenciales rechazadas'}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <button onClick={() => review('aprobado')} disabled={saving} style={{
                background: '#4ade8022', color: '#4ade80', border: '1px solid #4ade8044',
                borderRadius: 8, padding: '7px 16px', fontSize: 12, cursor: 'pointer',
                fontFamily: 'Outfit', fontWeight: 600, opacity: saving ? 0.5 : 1,
              }}> Aprobar</button>
              {!showReject && (
                <button onClick={() => setShowReject(true)} style={{
                  background: '#f8717122', color: '#f87171', border: '1px solid #f8717144',
                  borderRadius: 8, padding: '7px 16px', fontSize: 12, cursor: 'pointer',
                  fontFamily: 'Outfit', fontWeight: 600,
                }}> Rechazar</button>
              )}
              {showReject && (
                <div style={{ flex: '1 1 100%' }}>
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="Motivo del rechazo (ej: documento ilegible, COPNIA no verificada)..."
                    rows={2}
                    style={{
                      width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: 8,
                      color: C.text, padding: '8px 10px', fontSize: 12, fontFamily: 'Outfit',
                      boxSizing: 'border-box', resize: 'vertical', marginBottom: 8,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => review('rechazado')} disabled={saving || !rejectReason.trim()} style={{
                      background: '#f8717122', color: '#f87171', border: '1px solid #f8717144',
                      borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
                      fontFamily: 'Outfit', fontWeight: 600, opacity: (!rejectReason.trim() || saving) ? 0.4 : 1,
                    }}>Confirmar rechazo</button>
                    <button onClick={() => setShowReject(false)} style={{
                      background: 'none', color: C.muted, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'Outfit',
                    }}>Cancelar</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InstallersMgr({ installers, ss }) {
  const [sel, setSel] = useState(null);
  const [credReview, setCredReview] = useState(null);

  if (sel) {
    const inst = installers.find(x => x.id === sel);
    if (!inst) { setSel(null); return null; }
    return (
      <div>
        <button style={{ ...ss.btn, marginBottom: 12, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }} onClick={() => { setSel(null); setCredReview(null); }}>← Volver</button>
        <div style={ss.card}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.heading, marginBottom: 4 }}>{inst.name}</div>
          <div style={{ color: C.muted, fontSize: 11, marginBottom: 14 }}>{inst.company && `${inst.company} · `}{inst.dept} · {inst.date}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
            {[['Email', inst.email], ['Teléfono', inst.phone], ['Departamento', inst.dept], ['RETIE', inst.retie], ['Experiencia', inst.years + ' años'], ['Capacidad', inst.maxKwp + ' kWp/mes'], ['Tipos de proyecto', inst.types?.join(', ') || '—']].map(([k, v]) => (
              <div key={k} style={{ padding: '5px 0', borderBottom: `1px solid ${C.border}22` }}>
                <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase' }}>{k}</div>
                <div style={{ fontSize: 11, color: C.heading, fontWeight: 500, marginTop: 1 }}>{v}</div>
              </div>
            ))}
          </div>
          {/* Credential review toggle */}
          <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
            {credReview !== inst.id ? (
              <button onClick={() => setCredReview(inst.id)} style={{
                background: `${C.teal}18`, color: C.teal, border: `1px solid ${C.teal}44`,
                borderRadius: 8, padding: '7px 16px', fontSize: 12, cursor: 'pointer',
                fontFamily: 'Outfit', fontWeight: 600,
              }}> Verificar credenciales RETIE</button>
            ) : (
              <CredReviewPanel inst={inst} onClose={() => setCredReview(null)} ss={ss} />
            )}
          </div>
        </div>
      </div>
    );
  }

  const credBadge = (inst) => {
    const cs = inst.credential_status || 'pendiente';
    const b = CRED_STATUS_BADGE[cs] || CRED_STATUS_BADGE.pendiente;
    return (
      <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 9, background: b.bg, color: b.color, marginLeft: 4 }}>
        {b.icon} {cs}
      </span>
    );
  };

  return (
    <div>
      <div style={ss.h2}>Técnicos ({installers.length})</div>
      {installers.length === 0 ? (
        <div style={{ ...ss.card, textAlign: 'center', padding: '44px', color: C.muted }}>Los instaladores registrados aparecerán aquí.</div>
      ) : (
        <div style={ss.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Fecha', 'Nombre', 'Departamento', 'RETIE', 'Estado', 'Credenciales', ''].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
            <tbody>{installers.map(i => (
              <tr key={i.id}>
                <td style={ss.td}>{i.date}</td><td style={ss.td}>{i.name}</td><td style={ss.td}>{i.dept}</td><td style={ss.td}>{i.retie}</td>
                <td style={ss.td}><span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 9, background: `${C.yellow}22`, color: C.yellow }}>{i.status}</span></td>
                <td style={ss.td}>{credBadge(i)}</td>
                <td style={ss.td}><button style={{ ...ss.btn, padding: '2px 9px' }} onClick={() => setSel(i.id)}>Ver →</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <FichasModeladoTabla
        fichas={fichasUnion(
          { panels: DEFAULT_PANELS, inverters: DEFAULT_INVERTERS, batteries: DEFAULT_BATTERIES },
          leerOfertasProveedores(),
        )}
        ss={ss}
      />
    </div>
  );
}

function OperatorsMgr({ operators, upd, ss }) {
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [spot, setSpot] = useState(null);
  const [manualSpot, setManualSpot] = useState(() => {
    try { const v = localStorage.getItem('xm:manualSpot'); return v ? JSON.parse(v) : null; } catch { return null; }
  });
  const [editingSpot, setEditingSpot] = useState(false);
  const [spotInput, setSpotInput] = useState('');

  // 🔴 Estaba SOLO dentro del onClick del botón, y el `onKeyDown` del input llamaba a un
  // `saveManualSpot` que no existía en ningún lado: teclear la cifra y darle Enter —lo natural—
  // lanzaba un ReferenceError y no guardaba nada. El precio de bolsa manual es el respaldo que
  // usa AGPE cuando la API de XM no contesta, así que el modo de falla era el peor: se cree
  // guardado, y el excedente se sigue valorando con el número viejo.
  const saveManualSpot = () => {
    const v = parseFloat(spotInput);
    if (!isNaN(v) && v > 0) {
      const d = { cop_per_kwh: v, source: 'manual', syncedAt: new Date().toISOString() };
      setManualSpot(d);
      try { localStorage.setItem('xm:manualSpot', JSON.stringify(d)); } catch {}
    }
    setEditingSpot(false);
  };

  const startAdd = () => { setAdding(true); setEdit(null); setForm({ sic: '', name: '', fullName: '', region: '', tariff: 0, psh: 4.5 }); };
  const startEdit = o => { setEdit(o.name); setAdding(false); setForm({ ...o }); };
  const cancel = () => { setEdit(null); setAdding(false); setForm({}); };
  const save = () => {
    if (adding) upd([...operators, form]);
    else upd(operators.map(o => o.name === edit ? { ...form } : o));
    cancel();
  };
  const del = name => upd(operators.filter(o => o.name !== name));
  const resetDefaults = () => upd(OPERATORS);

  const isLocalDev = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(window.location.hostname);

  const syncXM = async () => {
    if (isLocalDev) {
      setSyncStatus({ warn: 'local-dev' });
      return;
    }
    if (!hubConfigurado()) {
      setSyncStatus({ warn: 'not-configured' });
      return;
    }
    setSyncStatus({ loading: true });
    try {
      const data = await fetchAgentsList();
      if (data?.ok === false) {
        setSyncStatus({ warn: 'error', msg: data.error || data.reason || 'no disponible', baseUrl: hubApiBaseUrl() });
        return;
      }
      const total = data.operators?.length ?? 0;
      if (!total) {
        setSyncStatus({
          warn: 'zero',
          rawPreview: data.rawPreview,
          syncedAt: data.syncedAt,
        });
        return;
      }
      const xmCodes = new Set(data.operators.map(o => o.sic).filter(Boolean));
      const matched = operators.filter(o => o.sic && xmCodes.has(o.sic)).length;
      setSyncStatus({ ok: true, total, matched, syncedAt: data.syncedAt, cached: data.cached, filterWarning: !data.activityFilterWorked });
    } catch (err) {
      const isNetwork = /Failed to fetch|NetworkError|AbortError|aborted/i.test(err.message || '');
      setSyncStatus({ warn: 'error', msg: err.message, baseUrl: hubApiBaseUrl(), network: isNetwork });
    }
  };

  const syncSpot = async () => {
    try {
      const p = await fetchSpotPrice(30);
      if (p?.ok === false) {
        setSpot({ error: p.error || p.reason });
        return;
      }
      setSpot(p);
    } catch (err) {
      setSpot({ error: err.message });
    }
  };

  const fields = [
    { k: 'sic', l: 'SIC' },
    { k: 'name', l: 'Nombre' },
    { k: 'fullName', l: 'Razón social' },
    { k: 'region', l: 'Departamento(s)' },
    { k: 'tariff', l: 'Tarifa COP/kWh', t: 'number' },
    { k: 'psh', l: 'PSH (h/día)', t: 'number' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ ...ss.h2, margin: 0 }}>Operadores de Red ({operators.length})</div>
        <div style={{ display: 'flex', gap: 6 }}>
 <button style={ss.btn} onClick={syncXM} disabled={syncStatus?.loading}>{syncStatus?.loading ? 'Sincronizando…' : '⟳ Sync XM'}</button>
          <button style={ss.btn} onClick={syncSpot}>$ Precio bolsa</button>
          <button style={{ ...ss.btn, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }} onClick={resetDefaults}>Restaurar defaults</button>
          <button style={ss.btn} onClick={startAdd}>+ Agregar</button>
        </div>
      </div>

      {syncStatus?.ok && (
        <div style={{ background: `${C.green}12`, border: `1px solid ${C.green}33`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.green }}>
 ✓ XM sync · {syncStatus.matched}/{operators.filter(o => o.sic).length} OR locales validados · {syncStatus.total} agentes XM {syncStatus.cached ? '(caché)' : ''} · {new Date(syncStatus.syncedAt).toLocaleString('es-CO')}
          {syncStatus.filterWarning && ' · Filtro actividad no detectó OR — incluidos todos los agentes.'}
        </div>
      )}
      {syncStatus?.warn === 'local-dev' && (
        <div style={{ background: `${C.yellow}15`, border: `1px solid ${C.yellow}44`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.yellow }}>
          ℹ Proxy XM no disponible en desarrollo local — los 20 operadores de la tabla son datos estáticos. Actualiza manualmente o despliega en Vercel.
        </div>
      )}
      {syncStatus?.warn === 'zero' && (
        <div style={{ background: `${C.yellow}15`, border: `1px solid ${C.yellow}44`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.yellow }}>
          ℹ XM ListadoAgentes no devolvió agentes en esta consulta — puede ser un cambio temporal de schema. La lista local de {operators.length} operadores permanece activa sin cambios.
          {syncStatus.rawPreview && <div style={{ marginTop: 5, fontFamily: 'monospace', fontSize: 9, color: C.muted, wordBreak: 'break-all' }}>Raw preview: {syncStatus.rawPreview}</div>}
        </div>
      )}
      {syncStatus?.warn === 'not-configured' && (
        <div style={{ background: `${C.yellow}15`, border: `1px solid ${C.yellow}44`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.yellow }}>
          ℹ Sync XM no configurado: no hay hub-api. La lista local de {operators.length} operadores permanece activa.
        </div>
      )}
      {syncStatus?.warn === 'error' && (
        <div style={{ background: `${C.yellow}15`, border: `1px solid ${C.yellow}44`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.yellow }}>
          <div>ℹ Sync XM no disponible: <strong>{syncStatus.msg}</strong>. La lista local permanece activa.</div>
          {syncStatus.baseUrl && (
            <div style={{ marginTop: 5, fontSize: 10, color: C.muted, wordBreak: 'break-all' }}>
              hub-api: <code style={{ fontFamily: 'monospace' }}>{syncStatus.baseUrl}/xm-agents</code>
              {syncStatus.network && (
                <div style={{ marginTop: 3 }}>
                  Revisa: (1) el hub-api responde, (2) CORS permite <code>{window.location.origin}</code>, (3) URL correcta, (4) DNS/SSL válido.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {spot?.cop_per_kwh != null && (
        <div style={{ background: `${C.yellow}12`, border: `1px solid ${C.yellow}33`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.yellow }}>
          $ Precio bolsa XM últimos {spot.periodDays}d: <strong>{spot.cop_per_kwh} COP/kWh</strong> · referencia para excedentes AGPE {spot.cached ? '(caché)' : ''}
        </div>
      )}
      {spot?.error && (
        <div style={{ background: `${C.red}12`, border: `1px solid ${C.red}33`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.red }}>
           Bolsa XM: {spot.error}
        </div>
      )}

      {/* Precio bolsa manual — activo mientras la API XM no responde */}
      <div style={{ background: `${C.yellow}10`, border: `1px solid ${C.yellow}25`, borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
        {!editingSpot ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontSize: 11, color: C.muted }}>
              $ Precio bolsa manual:{' '}
              {manualSpot
                ? <strong style={{ color: C.yellow }}>{manualSpot.cop_per_kwh} COP/kWh</strong>
                : <span style={{ color: C.muted, fontStyle: 'italic' }}>no definido — usado por AGPE si API falla</span>}
            </div>
            <button style={{ ...ss.btn, padding: '3px 10px', fontSize: 10 }} onClick={() => { setSpotInput(manualSpot?.cop_per_kwh || ''); setEditingSpot(true); }}>
              Editar
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" style={{ ...ss.inp, flex: 1, padding: '5px 8px', fontSize: 12 }}
              placeholder="Ej: 285.50" value={spotInput} onChange={e => setSpotInput(e.target.value)}
              autoFocus onKeyDown={e => { if (e.key === 'Enter') saveManualSpot(); if (e.key === 'Escape') setEditingSpot(false); }}
            />
            <span style={{ fontSize: 10, color: C.muted }}>COP/kWh</span>
            <button style={ss.btn} onClick={saveManualSpot}>Guardar</button>
 <button style={{ ...ss.btn, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }} onClick={() => setEditingSpot(false)}>✕</button>
          </div>
        )}
      </div>

      <div style={{ background: `${C.teal}10`, border: `1px solid ${C.teal}22`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
        Códigos SIC oficiales de XM (Sinergox). Tarifa CU (COP/kWh) = G + T + D + Cv + PR + R (CREG 091/2007). Los componentes se infieren de la tarifa plana con fracciones típicas N1 residencial hasta integrar PDFs mensuales por OR. Excedentes AGPE Menor se remuneran a CU − G (CREG 174/2021). PSH es estimación regional; PVGIS la sobreescribe en el cálculo.
      </div>

      {adding && (
        <div style={ss.card}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
            {fields.map(fl => (
              <div key={fl.k}><label style={ss.lbl}>{fl.l}</label>
                <input type={fl.t || 'text'} style={ss.inp} value={form[fl.k] || ''} onChange={e => setForm(p => ({ ...p, [fl.k]: fl.t === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
              <button style={ss.btn} onClick={save}>Guardar</button>
              <button style={{ ...ss.btn, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }} onClick={cancel}>×</button>
            </div>
          </div>
        </div>
      )}

      <div style={ss.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{[...fields.map(fl => fl.l), ''].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
          <tbody>
            {operators.map(op => edit === op.name
              ? (<tr key={op.name}><td colSpan={fields.length + 1} style={{ padding: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                    {fields.map(fl => (
                      <div key={fl.k}><label style={ss.lbl}>{fl.l}</label>
                        <input type={fl.t || 'text'} style={ss.inp} value={form[fl.k] ?? ''} onChange={e => setForm(p => ({ ...p, [fl.k]: fl.t === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))} />
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                      <button style={ss.btn} onClick={save}>Guardar</button>
                      <button style={{ ...ss.btn, background: 'transparent', border: `1px solid ${C.border}`, color: C.muted }} onClick={cancel}>×</button>
                    </div>
                  </div>
                </td></tr>)
              : (<tr key={op.name}>
                {fields.map(fl => {
                  if (fl.k === 'tariff') {
                    const cu = splitCU(op);
                    const exc = excedentePriceFor(op);
                    const tip = `CREG 091 (${cu.derived ? 'inferido' : 'explícito'}): G=${cu.G} · T=${cu.T} · D=${cu.D} · Cv=${cu.Cv} · PR=${cu.PR} · R=${cu.R}`;
                    return (
                      <td key={fl.k} style={ss.td} title={tip}>
                        <div>{op.tariff || cu.total || '—'}</div>
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>exc: {exc} <span style={{ opacity: 0.6 }}>(CU−G)</span></div>
                      </td>
                    );
                  }
                  return <td key={fl.k} style={ss.td}>{op[fl.k] || '—'}</td>;
                })}
                <td style={ss.td}>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <button style={ss.btn} onClick={() => startEdit(op)}>Editar</button>
                    <button style={ss.del} onClick={() => del(op.name)}>×</button>
                  </div>
                </td>
              </tr>)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Wrapper que combina el EqMgr local con un botón de importación CEC.
// Al importar, mezcla los specs eléctricos oficiales con valores razonables
// de precio/kg que el admin puede ajustar antes de guardar.
function PanelsTab({ panels, uP, ss }) {
  const [showCEC, setShowCEC] = useState(false);
  const [justImported, setJustImported] = useState(null);
  const onImport = (cec) => {
    const newPanel = {
      id: 'eq_' + Date.now(),
      brand: cec.brand || '',
      model: cec.model || '',
      wp: cec.wp || 0,
      price: 290000, // admin debe ajustar precio local
      kg: cec.length_m && cec.width_m ? parseFloat((cec.length_m * cec.width_m * 12).toFixed(1)) : 25,
      voc: cec.voc, vmp: cec.vmp, isc: cec.isc, imp: cec.imp,
      tempCoeffPmax: cec.tempCoeffPmax,
      tempCoeffVoc: cec.tempCoeffVoc,
      cellCount: cec.cellCount,
      technology: cec.technology,
      source: 'CEC',
    };
    uP([...panels, newPanel]);
    setJustImported(`${newPanel.brand} ${newPanel.model}`);
    setShowCEC(false);
    setTimeout(() => setJustImported(null), 3500);
  };
  return (
    <div>
      {justImported && (
        <div style={{ background: `${C.green}15`, border: `1px solid ${C.green}33`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.green }}>
 ✓ Importado desde CEC: <strong>{justImported}</strong>. Ajusta precio local antes de usar en el cotizador.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button style={{ ...ss.btn, background: C.yellow, color: '#050d12' }} onClick={() => setShowCEC(true)}> Importar desde CEC</button>
      </div>
      <EqMgr title="Paneles solares" items={panels} upd={uP} ss={ss} fields={[
        { k: 'brand', l: 'Marca', t: 'text' },
        { k: 'model', l: 'Modelo', t: 'text' },
        { k: 'wp', l: 'Wp', t: 'number' },
        { k: 'voc', l: 'Voc (V)', t: 'number' },
        { k: 'vmp', l: 'Vmp (V)', t: 'number' },
        { k: 'isc', l: 'Isc (A)', t: 'number' },
        { k: 'imp', l: 'Imp (A)', t: 'number' },
        { k: 'tempCoeffVoc', l: 'β Voc (%/°C)', t: 'number' },
        { k: 'price', l: 'Precio COP', t: 'number' },
      ]} />
      {showCEC && <CECImportModal type="panel" ss={ss} onClose={() => setShowCEC(false)} onImport={onImport} />}
    </div>
  );
}

function InvertersTab({ inverters, uI, ss }) {
  const [showCEC, setShowCEC] = useState(false);
  const [justImported, setJustImported] = useState(null);
  const onImport = (cec) => {
    const vac = cec.vac || 240;
    const type = vac >= 380 ? 'on-grid' : 'on-grid'; // por defecto; admin puede cambiar
    const newInv = {
      id: 'eq_' + Date.now(),
      brand: cec.brand || '',
      model: cec.model || '',
      kw: cec.kw ? parseFloat(cec.kw.toFixed(2)) : 0,
      phase: cec.phase || 1,
      vac,
      type,
      price: 2500000,
      kg: 20,
      vocMax: cec.vocMax,
      mpptVmin: cec.mpptVmin,
      mpptVmax: cec.mpptVmax,
      mpptCount: 2, // CEC no publica mpptCount; default típico
      idcMax: cec.idcMax,
      efficiency: 97.5,
      source: 'CEC',
    };
    uI([...inverters, newInv]);
    setJustImported(`${newInv.brand} ${newInv.model}`);
    setShowCEC(false);
    setTimeout(() => setJustImported(null), 3500);
  };
  return (
    <div>
      {justImported && (
        <div style={{ background: `${C.green}15`, border: `1px solid ${C.green}33`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.green }}>
 ✓ Importado desde CEC: <strong>{justImported}</strong>. Verifica tipo (on-grid/hybrid/off-grid), mpptCount y precio antes de usar.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button style={{ ...ss.btn, background: C.yellow, color: '#050d12' }} onClick={() => setShowCEC(true)}> Importar desde CEC</button>
      </div>
      <EqMgr title="Inversores" items={inverters} upd={uI} ss={ss} fields={[
        { k: 'brand', l: 'Marca', t: 'text' },
        { k: 'model', l: 'Modelo', t: 'text' },
        { k: 'kw', l: 'kW', t: 'number' },
        { k: 'type', l: 'Tipo', t: 'select', opts: ['on-grid', 'hybrid', 'off-grid'] },
        { k: 'vocMax', l: 'Vdc_max (V)', t: 'number' },
        { k: 'mpptVmin', l: 'MPPT min (V)', t: 'number' },
        { k: 'mpptVmax', l: 'MPPT max (V)', t: 'number' },
        { k: 'idcMax', l: 'Idc_max (A)', t: 'number' },
        { k: 'mpptCount', l: '# MPPT', t: 'number' },
        { k: 'price', l: 'Precio COP', t: 'number' },
      ]} />
      {showCEC && <CECImportModal type="inverter" ss={ss} onClose={() => setShowCEC(false)} onImport={onImport} />}
    </div>
  );
}

// Importador de baterías desde el catálogo curado (/api/batteries).
// Permite buscar por marca/modelo y filtrar por arquitectura (HV-stack/LV-48V).
function BatteryImportModal({ onClose, onImport, ss }) {
  const [q, setQ] = useState('');
  const [arch, setArch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const doSearch = async () => {
    setLoading(true); setError(null);
    try {
      const data = await searchBatteries(q, arch, 50);
      setResults(data.items || []);
    } catch (e) {
      setError(e.message);
      setResults([]);
    }
    setLoading(false);
  };

  // Auto-búsqueda al abrir y cuando cambia el filtro de arquitectura.
  React.useEffect(() => { doSearch(); /* eslint-disable-next-line */ }, [arch]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, width: 720, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.heading }}>Importar batería del catálogo</div>
          <button style={ss.del} onClick={onClose}>Cerrar</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input style={{ ...ss.inp, flex: 1 }} placeholder="Buscar: litio 48V, gel 48V, BESS (Pylontech, Ultracell, BYD…)" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()} />
          <select style={{ ...ss.inp, maxWidth: 150, cursor: 'pointer' }} value={arch} onChange={e => setArch(e.target.value)}>
            <option value="">Todas las arquitecturas</option>
            <option value="HV-stack">HV stack</option>
            <option value="LV-48V">LV 48 V</option>
            <option value="LV-24V">LV 24 V</option>
          </select>
          <button style={ss.btn} onClick={doSearch}>Buscar</button>
        </div>
        {loading && <div style={{ color: C.muted, fontSize: 11, padding: 8 }}>Cargando...</div>}
        {error && <div style={{ color: '#f87171', fontSize: 11, padding: 8 }}>{error}</div>}
        {!loading && !error && results.length === 0 && <div style={{ color: C.muted, fontSize: 11, padding: 8 }}>Sin resultados.</div>}
        {results.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Marca', 'Modelo', 'Quím.', 'kWh', 'V', 'Arq.', 'Ciclos', ''].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
            <tbody>{results.map(b => (
              <tr key={b.id}>
                <td style={ss.td}>{b.brand}</td>
                <td style={ss.td}>{b.model}</td>
                <td style={ss.td}>{b.chemistry}</td>
                <td style={ss.td}>{b.kwh}</td>
                <td style={ss.td}>{b.v}</td>
                <td style={ss.td}>{b.arch}</td>
                <td style={ss.td}>{b.cycles}</td>
                <td style={ss.td}><button style={ss.btn} onClick={() => onImport(b)}>Importar</button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BatteriesTab({ batteries, uB, ss }) {
  const [showImp, setShowImp] = useState(false);
  const [justImported, setJustImported] = useState(null);
  const onImport = (b) => {
    const newBatt = {
      id: 'eq_' + Date.now(),
      brand: b.brand,
      model: b.model,
      kwh: b.kwh,
      v: b.v,
      chemistry: b.chemistry,
      arch: b.arch,
      cycles: b.cycles,
      dod: b.dod,
      warrantyYears: b.warrantyYears,
      kg: b.kgPerModule,
      price: 5500000, // admin debe ajustar precio local
      source: 'Catalog',
    };
    uB([...batteries, newBatt]);
    setJustImported(`${newBatt.brand} ${newBatt.model}`);
    setShowImp(false);
    setTimeout(() => setJustImported(null), 3500);
  };
  return (
    <div>
      {justImported && (
        <div style={{ background: `${C.green}15`, border: `1px solid ${C.green}33`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.green }}>
 ✓ Importada: <strong>{justImported}</strong>. Ajusta precio local antes de usar en el cotizador.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button style={{ ...ss.btn, background: C.yellow, color: '#050d12' }} onClick={() => setShowImp(true)}> Importar del catálogo</button>
      </div>
      <EqMgr title="Baterías" items={batteries} upd={uB} ss={ss} fields={[
        { k: 'brand', l: 'Marca', t: 'text' },
        { k: 'model', l: 'Modelo', t: 'text' },
        { k: 'kwh', l: 'kWh', t: 'number' },
        { k: 'v', l: 'V nom.', t: 'number' },
        { k: 'arch', l: 'Arq.', t: 'text' },
        { k: 'chemistry', l: 'Quím.', t: 'text' },
        { k: 'price', l: 'Precio COP', t: 'number' },
      ]} />
      {showImp && <BatteryImportModal ss={ss} onClose={() => setShowImp(false)} onImport={onImport} />}
    </div>
  );
}

// Gestor de envíos de proveedores. Permite revisar los PDFs enviados,
// marcar estado y descargar el archivo. El data URL se almacena en
// localStorage — ideal para un flujo manual de revisión.
/**
 * EL PADRÓN DE PROVEEDORES — conectado de verdad desde el 2026-09-07.
 *
 * 🔴 QUÉ ERA ESTO ANTES. Un panel que leía `localStorage` y cuyos botones «Aprobar» y «Eliminar»
 * **no tocaban la tabla `suppliers`** que `supplier-auth` consulta para dejar entrar al Portal. Y
 * la lista no se podía poblar nunca: `addSupp` —su único escritor— no tenía un solo llamador. El
 * dueño creía gobernar el acceso de un proveedor y no gobernaba nada. Estaba medido y avisado
 * desde el 21-ago (`panel-proveedores-inerte.test.js`), esperando la ruta de administración.
 *
 * 🔑 Ahora la lista sale de `GET /tienda/proveedores` y cada cambio va a
 * `POST /tienda/proveedores/:id/estado`. **`Activo` no es una etiqueta: es la puerta del Portal**
 * (`supplier-auth` exige `active = true`), y por eso el botón dice la consecuencia.
 */
function useProveedoresTienda() {
  const [estado, setEstado] = React.useState({ cargando: true, error: null, lista: [] });

  const cargar = React.useCallback(() => {
    setEstado((s) => ({ ...s, cargando: true, error: null }));
    return hubGet('tienda/proveedores', {}, { headers: { Authorization: `Bearer ${tokenParaBackend() || ''}` } })
      .then((r) => {
        if (!r || r.ok === false) throw new Error(r?.error || 'El hub-api no devolvió el padrón.');
        setEstado({ cargando: false, error: null, lista: Array.isArray(r.proveedores) ? r.proveedores : [] });
      })
      .catch((e) => setEstado({ cargando: false, error: e.message, lista: [] }));
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);
  return { ...estado, cargar };
}

function SuppliersMgr({ ss, proveedores }) {
  const [guardando, setGuardando] = React.useState(null);
  const [aviso, setAviso] = React.useState(null);
  const fichas = fichasUnion(
    { panels: DEFAULT_PANELS, inverters: DEFAULT_INVERTERS, batteries: DEFAULT_BATTERIES },
    leerOfertasProveedores(),
  );

  const cambiar = async (id, cambios) => {
    setGuardando(id); setAviso(null);
    try {
      const r = await hubPost(`tienda/proveedores/${id}/estado`, { ...cambios, token: tokenParaBackend() || '' });
      if (!r || r.ok === false) throw new Error(r?.error || 'No se pudo guardar.');
      setAviso(r.efecto || 'Guardado.');
      await proveedores.cargar();
    } catch (e) {
      setAviso(`No se pudo guardar: ${e.message}`);
    } finally {
      setGuardando(null);
    }
  };

  const lista = proveedores.lista;
  return (
    <div>
      <div style={ss.h2}>Proveedores de la Tienda</div>
      <div style={ss.card}>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.55 }}>
          Este es el padrón real: sale del servidor y lo que se cambie acá <strong>cambia el acceso al
          Portal de proveedores</strong> (<code>supplier-auth</code> exige que el proveedor esté activo).
          Su inventario es el que alimenta la Tienda y del que se sella el precio.
        </div>

        {proveedores.cargando && <div style={{ fontSize: 11, color: C.muted }}>Cargando el padrón…</div>}
        {proveedores.error && (
          <div style={{ fontSize: 11, color: '#f87171', marginBottom: 10 }}>
            No se pudo leer el padrón: {proveedores.error}. Estar vacío acá no dice cuántos proveedores hay.
          </div>
        )}
        {aviso && <div style={{ fontSize: 11, color: C.teal, marginBottom: 10 }}>{aviso}</div>}

        {!proveedores.cargando && !proveedores.error && lista.length === 0 && (
          <div style={{ color: C.muted, textAlign: 'center', padding: 24, fontSize: 12 }}>
            No hay proveedores registrados todavía.
          </div>
        )}

        {lista.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Empresa', 'NIT', 'Ciudad', 'Ítems', 'Revisión', 'Portal', ''].map(h => (
                <th key={h} style={ss.th}>{h}</th>
              ))}</tr></thead>
              <tbody>{lista.map(s => (
                <tr key={s.id}>
                  <td style={ss.td}>
                    <div style={{ fontWeight: 600 }}>{s.company}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{s.email}</div>
                  </td>
                  <td style={ss.td}>{s.nit || '—'}</td>
                  <td style={ss.td}>{s.city || '—'}</td>
                  <td style={ss.td}>{s.items}</td>
                  <td style={ss.td}>
                    <select
                      style={{ ...ss.inp, padding: '3px 6px', fontSize: 10 }}
                      value={s.status || 'pendiente'}
                      disabled={guardando === s.id}
                      onChange={e => cambiar(s.id, { status: e.target.value })}
                    >
                      <option value="pendiente">Pendiente</option>
                      <option value="revisado">Revisado</option>
                      <option value="integrado">Integrado</option>
                      <option value="rechazado">Rechazado</option>
                    </select>
                  </td>
                  <td style={ss.td}>
                    <span style={{ color: s.active ? C.teal : '#f87171', fontSize: 11, fontWeight: 600 }}>
                      {s.active ? 'Entra' : 'Cerrado'}
                    </span>
                    {s.verified ? <div style={{ fontSize: 10, color: C.muted }}>verificado</div> : null}
                  </td>
                  <td style={ss.td}>
                    <button
                      type="button"
                      style={s.active ? ss.del : { ...ss.btn, padding: '4px 9px', fontSize: 10 }}
                      disabled={guardando === s.id}
                      onClick={() => cambiar(s.id, { activo: !s.active })}
                    >
                      {s.active ? 'Cerrarle el Portal' : 'Dejarlo entrar'}
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
      <FichasModeladoTabla fichas={fichas} ss={ss} />
    </div>
  );
}

// Cuadro de cargas — catálogo de referencia de consumos típicos para sistemas
// off-grid (usuarios sin recibo). Primario: `hub-api` /loads-catalog;
// fallback: lista local DEFAULT_LOADS_CATALOG. Cambios aquí sólo afectan la
// sesión local + caché (no persisten al backend remoto).
function LoadsTab({ catalog, source, setCatalog, setSource, ss }) {
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const uuid = () => `lc_${Math.random().toString(36).slice(2, 10)}`;

  const sync = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      invalidateLoadsCache();
      const d = await fetchLoadsCatalog();
      if (Array.isArray(d?.items) && d.items.length) {
        setCatalog && setCatalog(d.items);
        setSource && setSource(d.source || 'hub-api');
 setSyncMsg(`✓ ${d.items.length} cargas cargadas (${d.source || 'hub-api'})`);
      } else {
        setSyncMsg(' respuesta vacía — manteniendo catálogo local');
      }
    } catch (e) {
      setSyncMsg(` ${e?.message || 'error'} — usando catálogo local`);
    }
    setSyncing(false);
  };

  const items = Array.isArray(catalog) && catalog.length ? catalog : DEFAULT_LOADS_CATALOG;
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const cats = ['all', ...Array.from(new Set(items.map(i => i.category).filter(Boolean)))];
  const visible = items
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => (cat === 'all' || it.category === cat)
      && (!q.trim() || `${it.name || ''} ${it.category || ''}`.toLowerCase().includes(q.toLowerCase().trim())));

  const updateItem = (idx, key, val) => {
    const next = items.map((it, i) => i === idx ? { ...it, [key]: val } : it);
    setCatalog && setCatalog(next);
    setSource && setSource('custom');
  };
  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setCatalog && setCatalog(next);
    setSource && setSource('custom');
  };
  const addItem = () => {
    setCatalog && setCatalog([...items, { id: uuid(), name: '', watts: 0, hours: 0, qty: 1, category: '' }]);
    setSource && setSource('custom');
  };
  const resetLocal = () => {
    setCatalog && setCatalog(DEFAULT_LOADS_CATALOG);
    setSource && setSource('local-default');
  };

  return (
    <div>
      <div style={ss.h2}>Cuadro de cargas — catálogo de referencia</div>
      <div style={ss.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
            Fuente actual: <span style={{ color: C.teal, fontWeight: 700 }}>{source}</span>
            {' · '}
            {hubConfigurado()
              ? 'hub-api activo — puedes sincronizar el catálogo de cargas.'
              : 'hub-api no configurado — usando la lista por defecto.'}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={sync} disabled={syncing || !hubConfigurado()} style={{ ...ss.btn, opacity: (syncing || !hubConfigurado()) ? 0.5 : 1 }}>
              {syncing ? '⏳ Sincronizando…' : '↻ Sincronizar con hub-api'}
            </button>
            <button type="button" onClick={resetLocal} style={{ ...ss.btn, background: 'transparent', border: `1px solid ${C.border}`, color: C.heading }}>
              Restablecer por defecto
            </button>
            <button type="button" onClick={addItem} style={{ ...ss.btn, background: C.yellow, color: '#000' }}>+ Agregar carga</button>
          </div>
        </div>
 {syncMsg && <div style={{ fontSize: 11, color: syncMsg.startsWith('✓') ? C.teal : C.orange, marginBottom: 8 }}>{syncMsg}</div>}
        {/* Filtros: búsqueda + categoría */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar carga…" style={{ ...ss.inp, width: 220, padding: '6px 10px' }} />
          <select value={cat} onChange={e => setCat(e.target.value)} style={{ ...ss.inp, width: 180, padding: '6px 10px', cursor: 'pointer' }}>
            {cats.map(c => <option key={c} value={c}>{c === 'all' ? `Todas las categorías (${items.length})` : c}</option>)}
          </select>
          <span style={{ fontSize: 10, color: C.muted }}>{visible.length} de {items.length}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead>
              <tr>
                {['Carga', 'Categoría', 'Watts', 'Horas/día', 'Cant. típica', 'kWh/día', ''].map(h => <th key={h} style={ss.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {visible.map(({ it, idx }) => {
                const kwhDay = (Number(it.watts || 0) * Number(it.hours || 0) * Number(it.qty || 0)) / 1000;
                return (
                  <tr key={it.id || idx}>
                    <td style={ss.td}><input style={ss.inp} value={it.name || ''} onChange={e => updateItem(idx, 'name', e.target.value)} /></td>
                    <td style={ss.td}><input style={ss.inp} value={it.category || ''} onChange={e => updateItem(idx, 'category', e.target.value)} /></td>
                    <td style={ss.td}><input type="number" style={ss.inp} value={it.watts || 0} onChange={e => updateItem(idx, 'watts', Number(e.target.value))} /></td>
                    <td style={ss.td}><input type="number" step="0.1" style={ss.inp} value={it.hours || 0} onChange={e => updateItem(idx, 'hours', Number(e.target.value))} /></td>
                    <td style={ss.td}><input type="number" style={ss.inp} value={it.qty || 1} onChange={e => updateItem(idx, 'qty', Number(e.target.value))} /></td>
                    <td style={{ ...ss.td, color: C.teal, fontWeight: 600 }}>{kwhDay.toFixed(2)}</td>
                    <td style={ss.td}><button type="button" onClick={() => removeItem(idx)} style={ss.del}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
          Esta tabla alimenta el botón <strong style={{ color: C.teal }}>+ cargas típicas</strong> del cotizador cuando el usuario marca sistema off-grid. Los primeros 6 ítems se usan como preset inicial.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// USUARIOS — administración de usuarios de Solar Hub + ASIGNACIÓN TÉCNICO ↔ USUARIO.
// Infraestructura COMPARTIDA con FluxAI (`services/usuarios.js` espejo de FluxAI
// `web/src/lib/api.ts`). Misma mecánica que la pestaña "Usuarios" de FluxAI: alta de
// usuario, activar/desactivar, asignar un técnico (reemplaza el anterior) y listado.
// ─────────────────────────────────────────────────────────────────────────────
function UsuariosMgr({ quotes = [], installers = [], ss, sincro = 'ok' }) {
  const ROL_LBL = ROL_LABELS; // muestra cualquier rol (incl. interno heredado); el dropdown solo ofrece USER_ROLES
  const [tick, setTick] = React.useState(0);
  const refrescar = () => setTick(t => t + 1);
  React.useEffect(() => {
    const h = () => refrescar();
    window.addEventListener('solarhub-usuarios', h);
    syncUsuarios();  // hidrata usuarios + asignaciones desde el backend (cross-device)
    return () => window.removeEventListener('solarhub-usuarios', h);
  }, []);

  // Usuarios (siembra desde quotes) + técnicos (usuarios rol técnico + instaladores registrados).
  const usuarios = React.useMemo(() => listarUsuarios(undefined, quotes), [quotes, tick]);
  const clientesUsuarios = usuarios.filter(u => u.rol === 'usuario');
  const tecnicos = React.useMemo(() => {
    const base = usuarios.filter(u => u.rol === 'tecnico');
    // sumar instaladores registrados como técnicos seleccionables (id negativo para no chocar)
    const extra = (installers || []).filter(i => i.email).map(i => ({ id: -(i.id || 0) - 1, nombre: i.name || i.company || i.email, email: i.email, rol: 'tecnico', _instalador: true }));
    const seen = new Set(base.map(t => (t.email || '').toLowerCase()));
    return [...base, ...extra.filter(e => !seen.has((e.email || '').toLowerCase()))];
  }, [usuarios, installers]);
  const asignaciones = React.useMemo(() => listarAsignaciones(), [tick]);

  const nombreDe = (id) => usuarios.find(u => u.id === id)?.nombre || tecnicos.find(t => t.id === id)?.nombre || `#${id}`;

  // Alta de usuario
  const [nNombre, setNNombre] = React.useState('');
  const [nEmail, setNEmail] = React.useState('');
  const [nRol, setNRol] = React.useState('usuario');
  const [nTel, setNTel] = React.useState('');
  const [altaMsg, setAltaMsg] = React.useState(null);
  const crear = () => {
    setAltaMsg(null);
    try { crearUsuarioBasico({ nombre: nNombre, email: nEmail, rol: nRol, telefono: nTel || null }); setNNombre(''); setNEmail(''); setNTel(''); setNRol('usuario'); }
    catch (e) { setAltaMsg(e.message); }
  };

  // Asignación técnico ↔ usuario
  const [aCli, setACli] = React.useState('');
  const [aTec, setATec] = React.useState('');
  const [asigMsg, setAsigMsg] = React.useState(null);
  const asignar = () => {
    setAsigMsg(null);
    try {
      asignarTecnico(Number(aCli), Number(aTec));
      setAsigMsg(`${nombreDe(Number(aCli))} asignado a ${nombreDe(Number(aTec))}.`);
      setACli(''); setATec('');
    } catch (e) { setAsigMsg(e.message); }
  };

  const sel = { ...ss.inp, cursor: 'pointer' };

  return (
    <div>
      <div style={ss.h2}>Usuarios</div>

      {/* Banner de infraestructura compartida */}
      <div style={{ ...ss.card, borderLeft: `3px solid ${C.teal}`, fontSize: 11.5, color: C.muted, lineHeight: 1.6 }}>
        <strong style={{ color: C.heading }}>Técnico de cuenta</strong> (este panel): quién atiende al usuario, un técnico activo.
        El técnico de <strong style={{ color: C.heading }}>obra</strong> se asigna en Cotizaciones cuando la propuesta está ganada (matching RETIE → orden).
        No son lo mismo. Espejo FluxAI: `tecnico_usuarios` (cuenta) vs órdenes de campo.
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 9, marginBottom: 14 }}>
        {[['Usuarios', clientesUsuarios.length, C.teal], ['Técnicos', tecnicos.length, '#F9B000'], ['Asignados', asignaciones.length, C.heading], ['Sin técnico', clientesUsuarios.filter(u => !asignaciones.some(a => a.cliente_id === u.id)).length, '#f87171']].map(([l, v, col]) => (
          <div key={l} style={ss.stat}><div style={{ fontSize: 9, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>{l}</div><div style={{ fontSize: 18, fontWeight: 700, color: col }}>{v}</div></div>
        ))}
      </div>

      {/* Alta de usuario */}
      <div style={ss.card}>
        <div style={{ fontWeight: 600, color: C.heading, marginBottom: 11, fontSize: 13 }}>Crear usuario</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 9, alignItems: 'end' }}>
          <div><label style={ss.lbl}>Nombre</label><input style={ss.inp} value={nNombre} onChange={e => setNNombre(e.target.value)} placeholder="Nombre y apellido" /></div>
          <div><label style={ss.lbl}>Correo</label><input style={ss.inp} value={nEmail} onChange={e => setNEmail(e.target.value)} placeholder="correo@dominio.com" /></div>
          <div><label style={ss.lbl}>Rol</label>
            <select style={sel} value={nRol} onChange={e => setNRol(e.target.value)}>
              {USER_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div><label style={ss.lbl}>Teléfono</label><input style={ss.inp} value={nTel} onChange={e => setNTel(e.target.value)} placeholder="(opcional)" /></div>
          <div><button onClick={crear} disabled={!nEmail.trim()} style={{ ...ss.btn, width: '100%', opacity: nEmail.trim() ? 1 : 0.5 }}>+ Crear</button></div>
        </div>
        {altaMsg && <div style={{ fontSize: 11, color: '#f87171', marginTop: 8 }}>{altaMsg}</div>}
      </div>

      {/* Asignar técnico a un usuario (espejo FluxAI) */}
      <div style={ss.card}>
        <div style={{ fontWeight: 600, color: C.heading, marginBottom: 11, fontSize: 13 }}>Asignar técnico a un usuario</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr)) auto', gap: 9, alignItems: 'end' }}>
          <div><label style={ss.lbl}>Usuario (cliente)</label>
            <select style={sel} value={aCli} onChange={e => setACli(e.target.value)}>
              <option value="">{clientesUsuarios.length ? 'Elige un usuario…' : 'Sin usuarios'}</option>
              {clientesUsuarios.map(u => <option key={u.id} value={u.id}>{u.nombre} · {u.email}</option>)}
            </select>
          </div>
          <div><label style={ss.lbl}>Técnico</label>
            <select style={sel} value={aTec} onChange={e => setATec(e.target.value)}>
              <option value="">{tecnicos.length ? 'Elige un técnico…' : 'Sin técnicos'}</option>
              {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre} · {t.email}{t._instalador ? ' (instalador)' : ''}</option>)}
            </select>
          </div>
          <div><button onClick={asignar} disabled={!aCli || !aTec} style={{ ...ss.btn, opacity: (aCli && aTec) ? 1 : 0.5 }}>Asignar</button></div>
        </div>
        {asigMsg && <div style={{ fontSize: 11, color: C.teal, marginTop: 8 }}>{asigMsg}</div>}
        <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>Un usuario tiene un único técnico activo: si ya tenía uno, esta acción lo reemplaza.</div>
      </div>

      {/* Asignaciones técnico ↔ usuario */}
      <div style={ss.card}>
        <div style={{ fontWeight: 600, color: C.heading, marginBottom: 11, fontSize: 13 }}>Asignaciones técnico ↔ usuario</div>
        {asignaciones.length === 0 ? (
          <div style={{ color: C.muted, textAlign: 'center', padding: '20px', fontSize: 12 }}>Sin asignaciones todavía. Asigna un técnico a un usuario arriba.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Usuario', 'Técnico', 'Desde', 'Acción'].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
            <tbody>{asignaciones.map(a => (
              <tr key={a.id}>
                <td style={ss.td}>{nombreDe(a.cliente_id)}</td>
                <td style={ss.td}>{nombreDe(a.tecnico_id)}</td>
                <td style={ss.td}>{a.creado_en ? new Date(a.creado_en).toLocaleDateString('es-CO') : '—'}</td>
                <td style={ss.td}><button onClick={() => desasignarTecnico(a.cliente_id)} style={ss.del}>Quitar</button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {/* Listado de usuarios con activar/desactivar */}
      <div style={ss.card}>
        <div style={{ fontWeight: 600, color: C.heading, marginBottom: 11, fontSize: 13 }}>Todos los usuarios</div>
        {usuarios.length === 0 ? (
          <div style={{ color: C.muted, textAlign: 'center', padding: '20px', fontSize: 12 }}>{sincro === 'fallo' ? 'No se pudo consultar el padrón: la consulta al servidor no respondió. Esto NO significa que no haya usuarios — el padrón del servidor puede estar intacto.' : 'Aún no hay usuarios. Las cotizaciones generan usuarios automáticamente.'}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Nombre', 'Correo', 'Rol', 'Técnico asignado', 'Estado', 'Acción'].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
            <tbody>{usuarios.map(u => {
              const asig = asignaciones.find(a => a.cliente_id === u.id);
              return (
                <tr key={u.id}>
                  <td style={ss.td}>{u.nombre}</td>
                  <td style={ss.td}>{u.email}</td>
                  <td style={ss.td}>
                    {USER_ROLES.some(r => r.id === u.rol) ? (
                      <select value={u.rol} onChange={e => actualizarUsuario(u.id, { rol: e.target.value })} style={{ ...sel, padding: '3px 6px', fontSize: 10.5, width: 'auto' }}>
                        {USER_ROLES.map(r => <option key={r.id} value={r.id}>{ROL_LBL[r.id]}</option>)}
                      </select>
                    ) : (
                      // Rol INTERNO de Alebas (dueño/soporte/admin): no se reasigna desde acá.
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: C.teal }}>{ROL_LBL[u.rol] || u.rol} · interno</span>
                    )}
                  </td>
                  <td style={ss.td}>{u.rol === 'usuario' ? (asig ? nombreDe(asig.tecnico_id) : <span style={{ color: C.muted }}>—</span>) : <span style={{ color: C.muted }}>n/a</span>}</td>
                  <td style={ss.td}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: u.activo !== false ? '#1a7f3c' : '#c0392b' }}>{u.activo !== false ? 'Activo' : 'Inactivo'}</span>
                  </td>
                  <td style={ss.td}><button onClick={() => setActivoUsuario(u.id, u.activo === false)} style={{ ...ss.del, color: u.activo !== false ? '#f87171' : C.teal, borderColor: u.activo !== false ? '#f8717133' : `${C.teal}44` }}>{u.activo !== false ? 'Desactivar' : 'Activar'}</button></td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function BackOffice({ tab, setTab, panels, uP, inverters, uI, batteries, uB, pricing, uPr, operators, uOp, quotes, installers, suppliers = [], uSupp, loadsCatalog = [], loadsSource = 'local-default', setLoadsCatalog, setLoadsSource, ordenes = [], updOrden, sincro = {} }) {
  // Mismo criterio que en QuotesMgr: el porcentaje sale del hub-api, no de una constante.
  const comision = useComisionSuite('instalacion');
  // 🔑 El padrón REAL de proveedores, una sola vez para las dos pantallas que lo necesitan: el
  // panel de proveedores y el selector de «asignar proveedor» de una cotización — que hasta hoy
  // leía la lista de `localStorage` y por eso decía «Sin proveedores registrados» siempre.
  const proveedores = useProveedoresTienda();
  const [dashTrm, setDashTrm] = React.useState(null);
  React.useEffect(() => {
    fetchTRM().then(d => setDashTrm(d)).catch(() => {});
  }, []);

  const ss = {
    wrap: { display: 'flex', minHeight: 'calc(100vh - 54px)' },
    side: { width: 185, background: '#08151e', borderRight: `1px solid ${C.border}`, padding: '12px 8px', flexShrink: 0 },
    main: { flex: 1, padding: '20px 24px', overflowY: 'auto' },
    card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px', marginBottom: 12 },
    h2: { fontSize: 16, fontWeight: 700, color: C.heading, margin: '0 0 14px' },
    lbl: { display: 'block', fontSize: 10, color: C.muted, marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
    inp: { width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: 5, padding: '6px 9px', color: C.text, fontSize: 12, boxSizing: 'border-box' },
    btn: { padding: '6px 14px', background: C.teal, color: C.heading, border: 'none', borderRadius: 5, fontWeight: 600, cursor: 'pointer', fontSize: 11 },
    del: { padding: '4px 9px', background: 'transparent', color: '#f87171', border: '1px solid #f8717133', borderRadius: 4, cursor: 'pointer', fontSize: 10 },
    th: { padding: '5px 8px', textAlign: 'left', color: C.muted, borderBottom: `1px solid ${C.border}`, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 500 },
    td: { padding: '7px 8px', borderBottom: `1px solid ${C.border}22`, color: C.text, fontSize: 11 },
    stat: { background: C.dark, border: `1px solid ${C.border}`, borderRadius: 7, padding: '11px 13px' },
  };

  // Pestañas agrupadas: GESTIÓN / cierre de negocio  ·  CONFIGURACIÓN que alimenta el cotizador.
  const GROUPS = [
    ['Gestión', [['dashboard', 'Dashboard'], ['quotes', 'Cotizaciones'], ['usuarios', 'Usuarios'], ['installers', 'Técnicos'], ['suppliers', 'Proveedores'], ['servicios', 'DX Servicios'], ['facturacion', 'Pagos y Facturación'], ['empresa', 'PQR y padrón']]],
    ['Configuración del cotizador', [['operators', 'Operadores Red'], ['panels', 'Paneles'], ['inverters', 'Inversores'], ['batteries', 'Baterías'], ['loads', 'Cuadro de cargas'], ['pricing', 'Precios']]],
    ['Tienda Solar Hub', [['tienda', 'Catálogo y stock']]],
  ];

  const tot = quotes.length, nv = quotes.filter(q => q.status === 'nuevo').length;
  const kp = quotes.reduce((s, q) => s + parseFloat(q.results?.actKwp || 0), 0).toFixed(1);
  const pl = quotes.reduce((s, q) => s + (q.budget?.tot || 0), 0);

  return (
    <div style={{ padding: '18px 24px 40px', minHeight: 'calc(100vh - 54px)' }}>
      {/* Cabecera de la página (estilo FluxAI) */}
      <div style={{ borderLeft: `3px solid ${C.teal}`, paddingLeft: 12, marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.heading }}>Administración</h1>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Configura toda la plataforma y administra todos los servicios.</div>
      </div>
      {/* Pestañas agrupadas en DOS controles segmentados (estética sobria, theme-aware) */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap', marginBottom: 20 }}>
        {GROUPS.map(([seccion, items], gi) => {
          const acc = gi === 0 ? C.teal : C.yellow;   // Gestión = teal · Configuración = ámbar
          return (
            <div key={seccion} style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 9.5, color: acc, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 800, paddingLeft: 2 }}>
                <span style={{ width: 6, height: 6, borderRadius: 2, background: acc, display: 'inline-block' }} />{seccion}
              </span>
              <div style={{ display: 'inline-flex', gap: 3, flexWrap: 'wrap', background: `${C.muted}14`, border: `1px solid ${C.border}`, borderRadius: 11, padding: 4 }}>
                {items.map(([id, l]) => {
                  const on = tab === id;
                  return (
                    <button key={id} onClick={() => setTab(id)} style={{
                      padding: '7px 14px', border: 'none', borderRadius: 8,
                      background: on ? acc : 'transparent',
                      color: on ? '#fff' : C.muted, cursor: 'pointer', fontSize: 12, fontWeight: on ? 700 : 500,
                      boxShadow: on ? `0 1px 6px ${acc}55` : 'none',
                      whiteSpace: 'nowrap', transition: 'all .15s',
                    }}>{l}</button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <main style={{ minWidth: 0 }}>
        {tab === 'dashboard' && (
          <div>
            <div style={ss.h2}>Dashboard</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 9, marginBottom: 16 }}>
              {[['Cotizaciones', tot, C.teal], ['Sin procesar', nv, C.yellow], ['kWp cotizados', kp, C.heading], ['Pipeline', `$${fmt(pl / 1e6)}M`, C.teal]].map(([l, v, col]) => (
                <div key={l} style={ss.stat}><div style={{ fontSize: 9, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>{l}</div><div style={{ fontSize: 18, fontWeight: 700, color: col }}>{v}</div></div>
              ))}
              <div style={ss.stat}>
                <div style={{ fontSize: 9, color: C.muted, marginBottom: 4, textTransform: 'uppercase' }}>TRM COP/USD</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.yellow }}>{dashTrm?.cop_per_usd ? `$${fmt(dashTrm.cop_per_usd)}` : '—'}</div>
                {dashTrm?.date && <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{dashTrm.date}</div>}
              </div>
            </div>
            <div style={ss.card}>
              <div style={{ fontWeight: 600, color: C.heading, marginBottom: 11, fontSize: 13 }}>Últimas cotizaciones</div>
              {quotes.length === 0 ? <div style={{ color: C.muted, textAlign: 'center', padding: '24px', fontSize: 12 }}>El cotizador generará leads aquí automáticamente.</div> : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Fecha', 'Cliente', 'Operador', 'Sistema', 'kWp', 'Inversión'].map(h => <th key={h} style={ss.th}>{h}</th>)}</tr></thead>
                  <tbody>{quotes.slice(0, 6).map(q => (
                    <tr key={q.id}><td style={ss.td}>{q.date}</td><td style={ss.td}>{q.name}</td><td style={ss.td}>{q.operator || '—'}</td><td style={ss.td}>{q.systemType}</td><td style={ss.td}>{q.results?.actKwp}</td><td style={ss.td}>{q.budget ? fmtCOP(q.budget.tot) : '—'}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </div>

            {/* Commission summary — calculates from quotes prop */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: C.yellow, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 12 }}> Liquidación de plataforma (estimada)</div>
              {(() => {
                const lista = quotes.reduce((s, q) => s + precioListaEquipos(q), 0);
                const install = quotes.reduce((s, q) => s + baseInstalacionCotizacion(q), 0);
                const d = desgloseCierre({ lista, preferencial: lista, instalacion: install, cargoTecPct: comision.pct });
                const platformEquip = 0;
                const platformInstall = d.cargoAdminTec;
                const totalPlatform = platformEquip + platformInstall;
                return (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:10 }}>
                    {[
                      ['Cotizaciones', quotes.length, C.muted, ''],
                      ['Margen equipos', 'al cerrar PO', C.teal, 'lista − preferencial'],
                      [`Reinversión ${comision.pct}% obra`, `${Math.round(platformInstall/1000)}K`, '#F9B000', 'infra · capa · herramientas'],
                      ['Total plataforma', `$${Math.round(totalPlatform/1000000)}M`, C.yellow, '(est.)'],
                    ].map(([l,v,col,note])=>(
                      <div key={l} style={{ background:C.dark, borderRadius:8, padding:'10px 12px', border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:9, color:C.muted, textTransform:'uppercase', letterSpacing:0.5, marginBottom:3 }}>{l}</div>
                        <div style={{ fontSize:16, fontWeight:700, color:col }}>{v}</div>
                        {note && <div style={{ fontSize:9, color:C.muted }}>{note}</div>}
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div style={{ fontSize:9, color:C.muted, marginTop:10 }}>* Estimado sobre cotizaciones visibles. Las comisiones reales se registran al crear Órdenes de Compra (POs).</div>
            </div>
          </div>
        )}
        {tab === 'panels' && <PanelsTab panels={panels} uP={uP} ss={ss} />}
        {tab === 'inverters' && <InvertersTab inverters={inverters} uI={uI} ss={ss} />}
        {tab === 'batteries' && <BatteriesTab batteries={batteries} uB={uB} ss={ss} />}
        {tab === 'loads' && <LoadsTab catalog={loadsCatalog} source={loadsSource} setCatalog={setLoadsCatalog} setSource={setLoadsSource} ss={ss} />}
        {tab === 'pricing' && <PriceMgr pricing={pricing} upd={uPr} ss={ss} />}
        {tab === 'operators' && <OperatorsMgr operators={operators} upd={uOp} ss={ss} />}
        {tab === 'quotes' && <QuotesMgr quotes={quotes} suppliers={proveedores.lista} installers={installers} ordenes={ordenes} updOrden={updOrden} ss={ss} />}
        {tab === 'usuarios' && <UsuariosMgr quotes={quotes} installers={installers} ss={ss} sincro={sincro.usuarios} />}
        {tab === 'installers' && <InstallersMgr installers={installers} ss={ss} />}
        {tab === 'suppliers' && <SuppliersMgr ss={ss} proveedores={proveedores} />}
        {tab === 'servicios' && <ServicesDx ss={ss} />}
        {tab === 'facturacion' && <PagosFacturacion />}
        {tab === 'empresa' && <Empresa ss={ss} />}
        {tab === 'tienda' && <BackOfficeTienda ss={ss} />}
      </main>
    </div>
  );
}
